import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PaymentIcon from '@mui/icons-material/Payment';
import ReplayIcon from '@mui/icons-material/Replay';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { feeTypeOptions, paymentMethodOptions, paymentStatusOptions } from '../data/options';
import type { LookupState, UserPreferences } from '../types';
import { syncTeamFeeAssignments } from '../utils/feeAssignments';
import { formatPlayerName } from '../utils/playerPreferences';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { accessibleTeamSelection, restoreAccessibleTeamSelection, sameTeamSelection, shouldShowCaptainScopeEmpty } from '../utils/captainWorkflowScope';

type FeesPaymentsProps = {
  lookups: LookupState;
  defaultCaptainTeamIds: string[];
  userPreferences: UserPreferences;
  initialView?: 'campaigns' | 'outstanding' | 'credits' | 'payment-inbox' | 'payment-import-settings';
  readOnly?: boolean;
  canManageEmailImport?: boolean;
};

type FeeCampaign = {
  id: string;
  fee_name: string;
  fee_type: string;
  description: string | null;
  amount_per_player: number;
  assignment_granularity: 'per_team' | 'per_player';
  due_date: string | null;
  active_status: boolean;
  notes: string | null;
  created_at: string;
  created_by: string | null;
};

type FeeAssignment = {
  id: string;
  fee_campaign_id: string;
  organization_id: string | null;
  team_id: string | null;
  player_id: string;
  amount_owed: number;
  payment_status: string;
  payment_method: string | null;
  paid_date: string | null;
  notes: string | null;
};

type FeePayment = {
  id: string;
  fee_assignment_id: string;
  amount: number;
  cash_received_amount: number | null;
  fee_applied_cash_amount: number | null;
  credit_applied_amount: number | null;
  overpayment_credit_amount: number | null;
  overpayment_returned_amount: number | null;
  payment_date: string;
  payment_method: string;
  notes: string | null;
};

type FeeAssignmentFinancial = {
  fee_assignment_id: string;
  cash_paid: number;
  credit_applied: number;
  remaining_balance: number;
  computed_status: string;
};

type PlayerCreditBalance = {
  workspace_id: string;
  player_id: string;
  credit_balance: number;
  last_activity_at: string | null;
};

type PlayerLedgerEntry = {
  id: string;
  workspace_id: string;
  player_id: string;
  transaction_type: string;
  amount: number;
  description: string;
  source_fee_campaign_id: string | null;
  destination_fee_campaign_id: string | null;
  payment_id: string | null;
  related_ledger_entry_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
};

type WorkspacePaymentSettings = {
  id: string;
  name: string;
  payment_email_token: string;
  payment_email_import_mode: 'disabled' | 'review_only' | 'auto_exact';
  payment_email_last_received_at: string | null;
  payment_email_last_success_at: string | null;
  payment_email_last_error_at: string | null;
  payment_email_last_error: string | null;
};

type InboundPaymentEmail = {
  id: string;
  workspace_id: string;
  provider: 'Venmo' | 'Zelle' | null;
  from_header: string | null;
  subject: string | null;
  received_at: string;
  parsed_payer_name: string | null;
  parsed_amount: number | null;
  parsed_payment_memo: string | null;
  matched_player_id: string | null;
  matching_method: string | null;
  match_confidence: number | null;
  processing_status: string;
  parse_status: string;
  error_summary: string | null;
  parser_warnings: string[] | null;
};

const looksLikeInboundPayment = (email: InboundPaymentEmail) => /\b(paid you|received money with zelle|sent you money)\b/i.test(email.subject || '');
const isRecoverableIgnoredPayment = (email: InboundPaymentEmail) => email.processing_status === 'ignored'
  && ['needs_review', 'parse_failed'].includes(email.parse_status)
  && !email.provider && !email.parsed_payer_name && !email.parsed_amount
  && looksLikeInboundPayment(email);

type ImportedPaymentTransaction = {
  id: string;
  workspace_id: string;
  player_id: string | null;
  source_inbound_email_id: string;
  provider: 'Venmo' | 'Zelle';
  gross_amount: number;
  payment_status: string;
  amount_allocated_to_fees: number;
  amount_converted_to_credit: number;
  matching_method: string | null;
  reversed_at: string | null;
};

type ImportedPaymentAllocation = {
  id: string;
  imported_payment_transaction_id: string;
  amount_applied: number;
  balance_before: number;
  balance_after: number;
};

type PlayerPaymentAlias = {
  id: string;
  workspace_id: string;
  player_id: string;
  provider: 'Venmo' | 'Zelle' | null;
  alias_display_name: string;
  normalized_alias: string;
  is_active: boolean;
  is_verified: boolean;
};

type TeamRow = {
  id: string;
  team_name: string;
  organization_id: string | null;
};

type RosterRow = {
  team_id: string;
  player_id: string;
  roster_status: string | null;
  is_active: boolean;
};

type PlayerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
};

type PreviewAssignment = {
  key: string;
  organization_id: string | null;
  team_id: string | null;
  player_id: string;
  amount_owed: number;
};

type CoverageRow = {
  teamId: string;
  rosterPlayers: number;
  assignedPlayers: number;
  missingAssignments: number;
};

type AssignmentWithBalance = FeeAssignment & {
  amount_paid: number;
  cash_paid: number;
  credit_applied: number;
  outstanding: number;
  computed_status: string;
  latest_payment_method: string | null;
  latest_paid_date: string | null;
};

type Snack = { severity: 'success' | 'error'; message: string };
type CampaignAssignmentSortField = 'player' | 'organization' | 'team' | 'amount_owed' | 'cash_paid' | 'credit_applied' | 'outstanding' | 'status' | 'method' | 'paid_date';
type SortDirection = 'asc' | 'desc';
type PaymentInboxFilter = 'needs_review' | 'automatically_applied' | 'unmatched' | 'duplicate' | 'parse_failed' | 'ignored' | 'reversed' | 'all';
type FeesPaymentsViewState = {
  tab: 'campaigns' | 'outstanding' | 'credits' | 'payment-inbox' | 'payment-import-settings';
  selectedCampaignId: string | null;
  campaignFilters: {
    feeType: string;
    activeStatus: string;
    dueFrom: string;
    dueTo: string;
  };
  reportFilters: {
    organizationId: string;
    teamIds: string[];
    playerId: string;
    feeType: string;
    campaignId: string;
    status: string;
    dueFrom: string;
    dueTo: string;
    overdueOnly: boolean;
    includeZeroCredits: boolean;
  };
  teamScopeMode?: 'default' | 'explicit';
  paymentInboxFilter: PaymentInboxFilter;
};

const today = () => new Date().toISOString().slice(0, 10);

function money(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function optionLabel(options: { value: string; label: string }[], value: string | null | undefined): string {
  if (!value) return '';
  return options.find((option) => option.value === value)?.label || value;
}

function daysOverdue(dueDate: string | null, status: string): number {
  if (!dueDate || status === 'Paid' || status === 'Waived') return 0;
  const due = new Date(`${dueDate}T00:00:00`);
  const current = new Date(`${today()}T00:00:00`);
  return Math.max(0, Math.floor((current.getTime() - due.getTime()) / 86400000));
}

function dueLabel(dueDate: string | null): string {
  if (!dueDate) return '';
  const [year, month, day] = dueDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function computedStatus(assignment: FeeAssignment, paid: number): string {
  if (assignment.payment_status === 'Waived') return 'Waived';
  if (paid <= 0) return 'Unpaid';
  if (paid < Number(assignment.amount_owed || 0)) return 'Partially Paid';
  return 'Paid';
}

function compareText(a: string | null | undefined, b: string | null | undefined): number {
  return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base', numeric: true });
}

function compareNumber(a: number | null | undefined, b: number | null | undefined): number {
  return Number(a || 0) - Number(b || 0);
}

export function FeesPayments({ lookups, defaultCaptainTeamIds, userPreferences, initialView, readOnly = false, canManageEmailImport = false }: FeesPaymentsProps) {
  const auditUserId = useAuditUserId();
  const defaultViewState = useMemo<FeesPaymentsViewState>(() => ({
    tab: canManageEmailImport ? initialView || 'campaigns' : initialView === 'payment-import-settings' ? 'campaigns' : initialView || 'campaigns',
    selectedCampaignId: null,
    campaignFilters: { feeType: 'all', activeStatus: 'active', dueFrom: '', dueTo: '' },
    reportFilters: { organizationId: 'all', teamIds: defaultCaptainTeamIds, playerId: 'all', feeType: 'all', campaignId: 'all', status: 'all', dueFrom: '', dueTo: '', overdueOnly: false, includeZeroCredits: false },
    teamScopeMode: 'default',
    paymentInboxFilter: 'needs_review',
  }), [defaultCaptainTeamIds, initialView]);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'fees-payments',
    defaultState: defaultViewState,
  });
  const [tab, setTab] = useState<'campaigns' | 'outstanding' | 'credits' | 'payment-inbox' | 'payment-import-settings'>(canManageEmailImport && (initialView || savedViewState.tab) === 'payment-import-settings' ? 'payment-import-settings' : initialView === 'payment-import-settings' ? 'campaigns' : initialView || savedViewState.tab);
  const [campaigns, setCampaigns] = useState<FeeCampaign[]>([]);
  const [assignments, setAssignments] = useState<FeeAssignment[]>([]);
  const [payments, setPayments] = useState<FeePayment[]>([]);
  const [assignmentFinancials, setAssignmentFinancials] = useState<FeeAssignmentFinancial[]>([]);
  const [creditBalances, setCreditBalances] = useState<PlayerCreditBalance[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<PlayerLedgerEntry[]>([]);
  const [workspacePaymentSettings, setWorkspacePaymentSettings] = useState<WorkspacePaymentSettings[]>([]);
  const [inboundPaymentEmails, setInboundPaymentEmails] = useState<InboundPaymentEmail[]>([]);
  const [importedPaymentTransactions, setImportedPaymentTransactions] = useState<ImportedPaymentTransaction[]>([]);
  const [importedPaymentAllocations, setImportedPaymentAllocations] = useState<ImportedPaymentAllocation[]>([]);
  const [paymentAliases, setPaymentAliases] = useState<PlayerPaymentAlias[]>([]);
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [rosters, setRosters] = useState<RosterRow[]>([]);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(savedViewState.selectedCampaignId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);
  const [paymentAssignmentId, setPaymentAssignmentId] = useState<string | null>(null);
  const [historyAssignmentId, setHistoryAssignmentId] = useState<string | null>(null);
  const [ledgerPlayerId, setLedgerPlayerId] = useState<string | null>(null);
  const [applyCreditAssignmentId, setApplyCreditAssignmentId] = useState<string | null>(null);
  const [applyCreditDescriptionEdited, setApplyCreditDescriptionEdited] = useState(false);
  const [addAssignmentCampaignId, setAddAssignmentCampaignId] = useState<string | null>(null);
  const [campaignForm, setCampaignForm] = useState({
    fee_name: '',
    fee_type: 'Court Fees',
    description: '',
    amount_per_player: '0',
    assignment_granularity: 'per_team' as 'per_team' | 'per_player',
    due_date: '',
    notes: '',
    active_status: true,
  });
  const [assignmentScope, setAssignmentScope] = useState<'team' | 'teams' | 'players'>('team');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [individualTeamId, setIndividualTeamId] = useState('');
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_date: today(), payment_method: 'Venmo', notes: '', overpayment_action: 'retain_credit' as 'retain_credit' | 'return' });
  const [applyCreditForm, setApplyCreditForm] = useState({ sourcePlayerId: '', amount: '', description: '' });
  const [creditAdjustmentOpen, setCreditAdjustmentOpen] = useState(false);
  const [creditAdjustmentForm, setCreditAdjustmentForm] = useState({ workspaceId: '', playerId: '', transactionType: 'manual_credit', amount: '', description: '' });
  const [addAssignmentForm, setAddAssignmentForm] = useState({ teamId: '', playerIds: [] as string[], amount: '' });
  const [campaignFilters, setCampaignFilters] = useState(savedViewState.campaignFilters);
  const [campaignAssignmentSort, setCampaignAssignmentSort] = useState<{ field: CampaignAssignmentSortField; direction: SortDirection }>({ field: 'player', direction: 'asc' });
  const [reportFilters, setReportFilters] = useState(savedViewState.reportFilters);
  const [teamScopeMode, setTeamScopeMode] = useState<'default' | 'explicit'>(savedViewState.teamScopeMode || 'explicit');
  const [paymentInboxFilter, setPaymentInboxFilter] = useState<PaymentInboxFilter>(savedViewState.paymentInboxFilter);
  const [manualMatchByEmailId, setManualMatchByEmailId] = useState<Record<string, string>>({});
  const [aliasForm, setAliasForm] = useState({ workspaceId: '', playerId: '', provider: 'Venmo' as 'Venmo' | 'Zelle' | 'Any', alias: '' });
  const accessibleTeamIds = useMemo(() => lookups.teams.map((team) => team.value), [lookups.teams]);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  useEffect(() => {
    if (!canManageEmailImport && tab === 'payment-import-settings') setTab('campaigns');
  }, [canManageEmailImport, tab]);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'fees-payments', contextKey: tab, ready: !loading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setTab(canManageEmailImport && (initialView || savedViewState.tab) === 'payment-import-settings' ? 'payment-import-settings' : initialView === 'payment-import-settings' ? 'campaigns' : initialView || savedViewState.tab);
    setSelectedCampaignId(savedViewState.selectedCampaignId);
    setCampaignFilters(savedViewState.campaignFilters);
    setReportFilters(savedViewState.teamScopeMode === 'default'
      ? { ...savedViewState.reportFilters, teamIds: defaultCaptainTeamIds }
      : savedViewState.reportFilters);
    setTeamScopeMode(savedViewState.teamScopeMode || 'explicit');
    setPaymentInboxFilter(savedViewState.paymentInboxFilter);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, canManageEmailImport, defaultCaptainTeamIds, initialView, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ tab, selectedCampaignId, campaignFilters, reportFilters, teamScopeMode, paymentInboxFilter });
  }, [campaignFilters, paymentInboxFilter, reportFilters, selectedCampaignId, setSavedViewState, tab, teamScopeMode, viewStateKey, viewStateReady]);

  useEffect(() => {
    if (accessibleTeamIds.length === 0) return;
    const valid = restoreAccessibleTeamSelection(reportFilters.teamIds, defaultCaptainTeamIds, accessibleTeamIds);
    if (reportFilters.teamIds.length > 0 && accessibleTeamSelection(reportFilters.teamIds, accessibleTeamIds).length === 0) setTeamScopeMode('default');
    if (!sameTeamSelection(valid, reportFilters.teamIds)) setReportFilters((current) => ({ ...current, teamIds: valid }));
  }, [accessibleTeamIds, defaultCaptainTeamIds, reportFilters.teamIds]);

  useEffect(() => {
    if (teamScopeMode !== 'default' || accessibleTeamIds.length === 0) return;
    const scope = accessibleTeamSelection(defaultCaptainTeamIds, accessibleTeamIds);
    setReportFilters((current) => sameTeamSelection(current.teamIds, scope) ? current : { ...current, teamIds: scope });
  }, [accessibleTeamIds, defaultCaptainTeamIds, teamScopeMode]);

  const loadData = useCallback(async () => {
    if (accessibleTeamIds.length === 0) {
      setCampaigns([]);
      setAssignments([]);
      setPayments([]);
      setAssignmentFinancials([]);
      setCreditBalances([]);
      setLedgerEntries([]);
      setWorkspacePaymentSettings([]);
      setInboundPaymentEmails([]);
      setImportedPaymentTransactions([]);
      setImportedPaymentAllocations([]);
      setPaymentAliases([]);
      setTeams([]);
      setRosters([]);
      setPlayers([]);
      return;
    }
    setLoading(true);
    const teamQuery = supabase.from('teams').select('id, team_name, organization_id').is('deleted_at', null).eq('is_active', true).in('id', accessibleTeamIds).order('team_name');
    const rosterQuery = supabase.from('team_rosters').select('team_id, player_id, roster_status, is_active').is('deleted_at', null).eq('is_active', true).neq('roster_status', 'inactive').in('team_id', accessibleTeamIds);
    const assignmentQuery = supabase.from('player_fee_assignments').select('*').is('deleted_at', null).in('team_id', accessibleTeamIds);
    const [campaignResult, assignmentResult, paymentResult, financialResult, teamResult, rosterResult, playerResult] = await Promise.all([
      supabase.from('team_fee_campaigns').select('*').is('deleted_at', null).order('due_date', { ascending: true }),
      assignmentQuery,
      supabase.from('player_fee_payments').select('*').is('deleted_at', null).order('payment_date', { ascending: false }),
      supabase.from('player_fee_assignment_financials').select('*').in('team_id', accessibleTeamIds),
      teamQuery,
      rosterQuery,
      supabase.from('players').select('id, first_name, last_name').is('deleted_at', null).eq('is_active', true),
    ]);
    setLoading(false);

    const error = campaignResult.error || assignmentResult.error || paymentResult.error || financialResult.error || teamResult.error || rosterResult.error || playerResult.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const loadedAssignments = (assignmentResult.data || []) as unknown as FeeAssignment[];
    const teamWorkspaceIds = new Set(((teamResult.data || []) as unknown as TeamRow[]).map((team) => lookups.teams.find((option) => option.value === team.id)?.workspace_id).filter(Boolean) as string[]);
    const [balanceResult, ledgerResult, workspacePaymentResult, inboundEmailResult, importedPaymentResult, importedAllocationResult, aliasResult] = await Promise.all([
      teamWorkspaceIds.size > 0
        ? supabase.from('player_credit_balances').select('*').in('workspace_id', Array.from(teamWorkspaceIds))
        : Promise.resolve({ data: [], error: null }),
      teamWorkspaceIds.size > 0
        ? supabase.from('player_financial_ledger_entries').select('*').in('workspace_id', Array.from(teamWorkspaceIds)).order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
      teamWorkspaceIds.size > 0
        ? supabase.from('workspaces').select('id, name, payment_email_token, payment_email_import_mode, payment_email_last_received_at, payment_email_last_success_at, payment_email_last_error_at, payment_email_last_error').in('id', Array.from(teamWorkspaceIds)).order('name')
        : Promise.resolve({ data: [], error: null }),
      teamWorkspaceIds.size > 0
        ? supabase.from('inbound_payment_emails').select('*').in('workspace_id', Array.from(teamWorkspaceIds)).order('received_at', { ascending: false }).limit(200)
        : Promise.resolve({ data: [], error: null }),
      teamWorkspaceIds.size > 0
        ? supabase.from('imported_payment_transactions').select('*').in('workspace_id', Array.from(teamWorkspaceIds)).order('created_at', { ascending: false }).limit(200)
        : Promise.resolve({ data: [], error: null }),
      supabase.from('imported_payment_allocations').select('*').order('created_at', { ascending: false }).limit(500),
      teamWorkspaceIds.size > 0
        ? supabase.from('player_payment_aliases').select('*').in('workspace_id', Array.from(teamWorkspaceIds)).order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
    ]);
    const financeError = balanceResult.error || ledgerResult.error || workspacePaymentResult.error || inboundEmailResult.error || importedPaymentResult.error || importedAllocationResult.error || aliasResult.error;
    if (financeError) {
      setSnack({ severity: 'error', message: financeError.message || 'Unable to load payment email reconciliation data.' });
      return;
    }

    setCampaigns((campaignResult.data || []) as unknown as FeeCampaign[]);
    setAssignments(loadedAssignments);
    setPayments((paymentResult.data || []) as unknown as FeePayment[]);
    setAssignmentFinancials((financialResult.data || []) as unknown as FeeAssignmentFinancial[]);
    setCreditBalances((balanceResult.data || []) as unknown as PlayerCreditBalance[]);
    setLedgerEntries((ledgerResult.data || []) as unknown as PlayerLedgerEntry[]);
    setWorkspacePaymentSettings((workspacePaymentResult.data || []) as unknown as WorkspacePaymentSettings[]);
    setInboundPaymentEmails((inboundEmailResult.data || []) as unknown as InboundPaymentEmail[]);
    setImportedPaymentTransactions((importedPaymentResult.data || []) as unknown as ImportedPaymentTransaction[]);
    setImportedPaymentAllocations((importedAllocationResult.data || []) as unknown as ImportedPaymentAllocation[]);
    setPaymentAliases((aliasResult.data || []) as unknown as PlayerPaymentAlias[]);
    setTeams((teamResult.data || []) as unknown as TeamRow[]);
    setRosters((rosterResult.data || []) as unknown as RosterRow[]);
    setPlayers((playerResult.data || []) as unknown as PlayerRow[]);
  }, [accessibleTeamIds, lookups.teams]);

  useEffect(() => {
    if (initialView) setTab(initialView);
  }, [initialView]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);
  const playerById = useMemo(() => new Map(players.map((player) => [player.id, player])), [players]);
  const campaignById = useMemo(() => new Map(campaigns.map((campaign) => [campaign.id, campaign])), [campaigns]);
  const workspaceByTeamId = useMemo(() => new Map(lookups.teams.map((team) => [team.value, team.workspace_id || ''])), [lookups.teams]);
  const creditBalanceByWorkspacePlayer = useMemo(() => new Map(creditBalances.map((balance) => [`${balance.workspace_id}:${balance.player_id}`, Number(balance.credit_balance || 0)])), [creditBalances]);
  const financialByAssignment = useMemo(() => new Map(assignmentFinancials.map((row) => [row.fee_assignment_id, row])), [assignmentFinancials]);
  const paymentsByAssignment = useMemo(() => {
    const map = new Map<string, FeePayment[]>();
    payments.forEach((payment) => map.set(payment.fee_assignment_id, [...(map.get(payment.fee_assignment_id) || []), payment]));
    return map;
  }, [payments]);

  const playerCreditBalance = useCallback((playerId: string, workspaceId?: string | null) => {
    if (workspaceId) return creditBalanceByWorkspacePlayer.get(`${workspaceId}:${playerId}`) || 0;
    return creditBalances.filter((balance) => balance.player_id === playerId).reduce((sum, balance) => sum + Number(balance.credit_balance || 0), 0);
  }, [creditBalanceByWorkspacePlayer, creditBalances]);

  const assignmentsWithBalance = useMemo<AssignmentWithBalance[]>(() => {
    return assignments.map((assignment) => {
      const assignmentPayments = paymentsByAssignment.get(assignment.id) || [];
      const financial = financialByAssignment.get(assignment.id);
      const cashPaid = financial ? Number(financial.cash_paid || 0) : assignmentPayments.reduce((sum, payment) => sum + Number(payment.fee_applied_cash_amount ?? payment.amount ?? 0), 0);
      const creditApplied = financial ? Number(financial.credit_applied || 0) : 0;
      const amountPaid = cashPaid + creditApplied;
      const status = financial?.computed_status || computedStatus(assignment, amountPaid);
      const outstanding = status === 'Paid' || status === 'Waived' ? 0 : Math.max(0, Number(financial?.remaining_balance ?? Number(assignment.amount_owed || 0) - amountPaid));
      const latestPayment = assignmentPayments[0];
      return {
        ...assignment,
        amount_paid: amountPaid,
        cash_paid: cashPaid,
        credit_applied: creditApplied,
        outstanding,
        computed_status: status,
        latest_payment_method: assignment.payment_method || latestPayment?.payment_method || null,
        latest_paid_date: assignment.paid_date || latestPayment?.payment_date || null,
      };
    });
  }, [assignments, financialByAssignment, paymentsByAssignment]);

  const previewAssignments = useMemo<PreviewAssignment[]>(() => {
    const amount = Number(campaignForm.amount_per_player || 0);
    const selectedTeams = assignmentScope === 'team' ? selectedTeamIds.slice(0, 1) : selectedTeamIds;
    const preview: PreviewAssignment[] = [];
    const seenAssignments = new Set<string>();
    const addPreviewAssignment = (teamId: string, playerId: string) => {
      const team = teamById.get(teamId);
      if (!team) return;
      const key = `${teamId}:${playerId}`;
      if (seenAssignments.has(key)) return;
      seenAssignments.add(key);
      preview.push({
        key,
        organization_id: team.organization_id || null,
        team_id: teamId,
        player_id: playerId,
        amount_owed: amount,
      });
    };

    if (assignmentScope === 'team' || assignmentScope === 'teams') {
      const seenPlayers = new Set<string>();
      selectedTeams.forEach((teamId) => {
        rosters
          .filter((roster) => roster.team_id === teamId)
          .forEach((roster) => {
            if (campaignForm.assignment_granularity === 'per_player' && seenPlayers.has(roster.player_id)) return;
            seenPlayers.add(roster.player_id);
            addPreviewAssignment(teamId, roster.player_id);
          });
      });
    } else {
      const seenPlayers = new Set<string>();
      selectedPlayerIds.forEach((playerId) => {
        if (campaignForm.assignment_granularity === 'per_player' && seenPlayers.has(playerId)) return;
        seenPlayers.add(playerId);
        if (individualTeamId) {
          addPreviewAssignment(individualTeamId, playerId);
          return;
        }
        rosters
          .filter((roster) => roster.player_id === playerId)
          .forEach((roster) => {
            if (campaignForm.assignment_granularity === 'per_player' && preview.some((assignment) => assignment.player_id === playerId)) return;
            addPreviewAssignment(roster.team_id, playerId);
          });
      });
    }

    return preview;
  }, [assignmentScope, campaignForm.amount_per_player, campaignForm.assignment_granularity, individualTeamId, rosters, selectedPlayerIds, selectedTeamIds, teamById]);

  const campaignSummaries = useMemo(() => {
    // Fee campaigns are shared definitions across teams; this tab and player
    // credit balances remain workspace-wide. Outstanding rows have a team scope.
    return campaigns.map((campaign) => {
      const rows = assignmentsWithBalance.filter((assignment) => assignment.fee_campaign_id === campaign.id);
      return {
        campaign,
        teamCount: new Set(rows.map((row) => row.team_id).filter(Boolean)).size,
        playerCount: new Set(rows.map((row) => row.player_id)).size,
        totalOwed: rows.reduce((sum, row) => sum + Number(row.amount_owed || 0), 0),
        totalPaid: rows.reduce((sum, row) => sum + row.amount_paid, 0),
        totalCashPaid: rows.reduce((sum, row) => sum + row.cash_paid, 0),
        totalCreditApplied: rows.reduce((sum, row) => sum + row.credit_applied, 0),
        totalOutstanding: rows.reduce((sum, row) => sum + row.outstanding, 0),
      };
    });
  }, [assignmentsWithBalance, campaigns]);

  const filteredCampaignSummaries = useMemo(() => {
    return campaignSummaries
      .filter(({ campaign }) => campaignFilters.feeType === 'all' || campaign.fee_type === campaignFilters.feeType)
      .filter(({ campaign }) => campaignFilters.activeStatus === 'all' || (campaignFilters.activeStatus === 'active' ? campaign.active_status : !campaign.active_status))
      .filter(({ campaign }) => !campaignFilters.dueFrom || (campaign.due_date || '') >= campaignFilters.dueFrom)
      .filter(({ campaign }) => !campaignFilters.dueTo || (campaign.due_date || '') <= campaignFilters.dueTo)
      .sort((a, b) => b.totalOutstanding - a.totalOutstanding || (a.campaign.due_date || '').localeCompare(b.campaign.due_date || '') || a.campaign.fee_name.localeCompare(b.campaign.fee_name));
  }, [campaignFilters, campaignSummaries]);

  const selectedCampaign = selectedCampaignId ? campaignById.get(selectedCampaignId) || null : null;
  const baseSelectedCampaignAssignments = useMemo(
    () => assignmentsWithBalance.filter((assignment) => assignment.fee_campaign_id === selectedCampaignId),
    [assignmentsWithBalance, selectedCampaignId],
  );
  const selectedCampaignAssignments = useMemo(() => {
    const direction = campaignAssignmentSort.direction === 'asc' ? 1 : -1;
    const valueCompare = (a: AssignmentWithBalance, b: AssignmentWithBalance): number => {
      if (campaignAssignmentSort.field === 'player') return compareText(formatPlayerName(playerById.get(a.player_id), userPreferences), formatPlayerName(playerById.get(b.player_id), userPreferences));
      if (campaignAssignmentSort.field === 'organization') return compareText(optionLabel(lookups.organizations, a.organization_id), optionLabel(lookups.organizations, b.organization_id));
      if (campaignAssignmentSort.field === 'team') return compareText(optionLabel(lookups.teams, a.team_id), optionLabel(lookups.teams, b.team_id));
      if (campaignAssignmentSort.field === 'amount_owed') return compareNumber(a.amount_owed, b.amount_owed);
      if (campaignAssignmentSort.field === 'cash_paid') return compareNumber(a.cash_paid, b.cash_paid);
      if (campaignAssignmentSort.field === 'credit_applied') return compareNumber(a.credit_applied, b.credit_applied);
      if (campaignAssignmentSort.field === 'outstanding') return compareNumber(a.outstanding, b.outstanding);
      if (campaignAssignmentSort.field === 'status') return compareText(a.computed_status, b.computed_status);
      if (campaignAssignmentSort.field === 'method') return compareText(a.latest_payment_method, b.latest_payment_method);
      if (campaignAssignmentSort.field === 'paid_date') return compareText(a.latest_paid_date, b.latest_paid_date);
      return 0;
    };
    return [...baseSelectedCampaignAssignments].sort((a, b) => {
      const primary = valueCompare(a, b);
      if (primary !== 0) return primary * direction;
      return compareText(formatPlayerName(playerById.get(a.player_id), userPreferences), formatPlayerName(playerById.get(b.player_id), userPreferences))
        || compareText(optionLabel(lookups.teams, a.team_id), optionLabel(lookups.teams, b.team_id));
    });
  }, [baseSelectedCampaignAssignments, campaignAssignmentSort, lookups.organizations, lookups.teams, playerById, userPreferences]);
  const toggleCampaignAssignmentSort = (field: CampaignAssignmentSortField) => {
    setCampaignAssignmentSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  const campaignAssignmentSortCell = (field: CampaignAssignmentSortField, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={campaignAssignmentSort.field === field ? campaignAssignmentSort.direction : false} sx={{ fontWeight: 700 }}>
      <TableSortLabel
        active={campaignAssignmentSort.field === field}
        direction={campaignAssignmentSort.field === field ? campaignAssignmentSort.direction : 'asc'}
        onClick={() => toggleCampaignAssignmentSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
  const creditSourcesForWorkspace = useCallback((workspaceId?: string | null) => {
    if (!workspaceId) return [];
    return creditBalances
      .filter((balance) => balance.workspace_id === workspaceId && Number(balance.credit_balance || 0) > 0 && playerById.has(balance.player_id))
      .sort((a, b) => formatPlayerName(playerById.get(a.player_id), userPreferences).localeCompare(formatPlayerName(playerById.get(b.player_id), userPreferences)));
  }, [creditBalances, playerById, userPreferences]);

  const assignmentCreditSources = (assignment: AssignmentWithBalance) => creditSourcesForWorkspace(assignment.team_id ? workspaceByTeamId.get(assignment.team_id) : '');
  const assignmentCreditAvailable = (assignment: AssignmentWithBalance) => playerCreditBalance(assignment.player_id, assignment.team_id ? workspaceByTeamId.get(assignment.team_id) : '');
  const assignmentHasAvailableCreditSource = (assignment: AssignmentWithBalance) => assignmentCreditSources(assignment).length > 0;
  const buildApplyCreditDescription = (sourcePlayerId: string, assignment: AssignmentWithBalance | null) => {
    if (!assignment || !sourcePlayerId || sourcePlayerId === assignment.player_id) return '';
    const assignmentPlayerName = formatPlayerName(playerById.get(assignment.player_id), userPreferences);
    const sourcePlayerName = formatPlayerName(playerById.get(sourcePlayerId), userPreferences);
    return `Credit from ${sourcePlayerName} applied to ${assignmentPlayerName} fees.`;
  };
  const closeApplyCreditDialog = () => {
    setApplyCreditAssignmentId(null);
    setApplyCreditDescriptionEdited(false);
  };
  const openPaymentForAssignment = (assignment: AssignmentWithBalance) => {
    setPaymentAssignmentId(assignment.id);
    setPaymentForm((current) => ({ ...current, amount: String(assignment.outstanding || '') }));
  };
  const openApplyCreditForAssignment = (assignment: AssignmentWithBalance) => {
    const creditSources = assignmentCreditSources(assignment);
    const selectedSource = creditSources.find((source) => source.player_id === assignment.player_id) || creditSources[0];
    const availableCredit = Number(selectedSource?.credit_balance || 0);
    setApplyCreditAssignmentId(assignment.id);
    setApplyCreditDescriptionEdited(false);
    setApplyCreditForm({
      sourcePlayerId: selectedSource?.player_id || '',
      amount: String(Math.min(assignment.outstanding, availableCredit)),
      description: buildApplyCreditDescription(selectedSource?.player_id || '', assignment),
    });
  };
  const renderAssignmentActions = (assignment: AssignmentWithBalance, variant: 'row' | 'column' = 'row') => (
    <Stack
      direction={variant === 'row' ? 'row' : 'column'}
      spacing={variant === 'row' ? 0.5 : 0.75}
      justifyContent={variant === 'row' ? 'flex-end' : undefined}
      flexWrap={variant === 'row' ? 'wrap' : undefined}
      useFlexGap={variant === 'row'}
    >
      {!readOnly && <Button size="small" variant={variant === 'column' ? 'outlined' : 'text'} startIcon={<EditIcon />} onClick={() => void editAmount(assignment)}>Amount</Button>}
      {!readOnly && <Button size="small" variant={variant === 'column' ? 'contained' : 'text'} startIcon={<PaymentIcon />} onClick={() => openPaymentForAssignment(assignment)}>Payment</Button>}
      {!readOnly && assignmentHasAvailableCreditSource(assignment) && assignment.outstanding > 0 && <Button size="small" variant={variant === 'column' ? 'outlined' : 'text'} onClick={() => openApplyCreditForAssignment(assignment)}>Apply Credit</Button>}
      <Button size="small" variant={variant === 'column' ? 'outlined' : 'text'} startIcon={<HistoryIcon />} onClick={() => setHistoryAssignmentId(assignment.id)}>History</Button>
      <Button size="small" variant={variant === 'column' ? 'outlined' : 'text'} onClick={() => setLedgerPlayerId(assignment.player_id)}>Ledger</Button>
      {!readOnly && <Button size="small" variant={variant === 'column' ? 'outlined' : 'text'} onClick={() => void updateAssignment(assignment.id, { payment_status: 'Waived' })}>Waive</Button>}
      {!readOnly && <Button size="small" variant={variant === 'column' ? 'outlined' : 'text'} color="error" startIcon={<DeleteIcon />} onClick={() => void softDeleteAssignment(assignment.id)}>Remove</Button>}
    </Stack>
  );
  const selectedCampaignCoverage = useMemo<CoverageRow[]>(() => {
    if (!selectedCampaignId) return [];
    const assignedPlayerIds = new Set(selectedCampaignAssignments.map((assignment) => assignment.player_id));
    const teamIds = Array.from(new Set([
      ...selectedCampaignAssignments.map((assignment) => assignment.team_id).filter((teamId): teamId is string => Boolean(teamId)),
      ...(selectedCampaign?.assignment_granularity === 'per_player'
        ? rosters.filter((roster) => assignedPlayerIds.has(roster.player_id)).map((roster) => roster.team_id)
        : []),
    ]));
    return teamIds
      .map((teamId) => {
        const rosterPlayers = new Set(rosters.filter((roster) => roster.team_id === teamId).map((roster) => roster.player_id));
        const assignedPlayers = selectedCampaign?.assignment_granularity === 'per_player'
          ? new Set(Array.from(rosterPlayers).filter((playerId) => assignedPlayerIds.has(playerId)))
          : new Set(selectedCampaignAssignments.filter((assignment) => assignment.team_id === teamId).map((assignment) => assignment.player_id));
        let missingAssignments = 0;
        rosterPlayers.forEach((playerId) => {
          if (!assignedPlayers.has(playerId)) missingAssignments += 1;
        });
        return {
          teamId,
          rosterPlayers: rosterPlayers.size,
          assignedPlayers: assignedPlayers.size,
          missingAssignments,
        };
      })
      .sort((a, b) => optionLabel(lookups.teams, a.teamId).localeCompare(optionLabel(lookups.teams, b.teamId)));
  }, [lookups.teams, rosters, selectedCampaign, selectedCampaignAssignments, selectedCampaignId]);

  const outstandingRows = useMemo(() => {
    return assignmentsWithBalance
      .map((assignment) => ({ assignment, campaign: campaignById.get(assignment.fee_campaign_id) }))
      .filter((row): row is { assignment: AssignmentWithBalance; campaign: FeeCampaign } => Boolean(row.campaign))
      .filter(({ assignment }) => assignment.computed_status !== 'Paid' && assignment.computed_status !== 'Waived')
      .filter(({ assignment }) => reportFilters.organizationId === 'all' || assignment.organization_id === reportFilters.organizationId)
      .filter(({ assignment }) => !shouldShowCaptainScopeEmpty(reportFilters.teamIds, defaultCaptainTeamIds, teamScopeMode) && (reportFilters.teamIds.length === 0 || (assignment.team_id != null && reportFilters.teamIds.includes(assignment.team_id))))
      .filter(({ assignment }) => reportFilters.playerId === 'all' || assignment.player_id === reportFilters.playerId)
      .filter(({ campaign }) => reportFilters.feeType === 'all' || campaign.fee_type === reportFilters.feeType)
      .filter(({ campaign }) => reportFilters.campaignId === 'all' || campaign.id === reportFilters.campaignId)
      .filter(({ assignment }) => reportFilters.status === 'all' || assignment.computed_status === reportFilters.status)
      .filter(({ campaign }) => !reportFilters.dueFrom || (campaign.due_date || '') >= reportFilters.dueFrom)
      .filter(({ campaign }) => !reportFilters.dueTo || (campaign.due_date || '') <= reportFilters.dueTo)
      .filter(({ campaign, assignment }) => !reportFilters.overdueOnly || daysOverdue(campaign.due_date, assignment.computed_status) > 0)
      .sort((a, b) => {
        const overdueCompare = daysOverdue(b.campaign.due_date, b.assignment.computed_status) - daysOverdue(a.campaign.due_date, a.assignment.computed_status);
        if (overdueCompare) return overdueCompare;
        return (a.campaign.due_date || '9999-12-31').localeCompare(b.campaign.due_date || '9999-12-31') ||
          optionLabel(lookups.organizations, a.assignment.organization_id).localeCompare(optionLabel(lookups.organizations, b.assignment.organization_id)) ||
          optionLabel(lookups.teams, a.assignment.team_id).localeCompare(optionLabel(lookups.teams, b.assignment.team_id)) ||
          formatPlayerName(playerById.get(a.assignment.player_id), userPreferences).localeCompare(formatPlayerName(playerById.get(b.assignment.player_id), userPreferences));
      });
  }, [assignmentsWithBalance, campaignById, defaultCaptainTeamIds, lookups, playerById, reportFilters, teamScopeMode, userPreferences]);

  const creditReportRows = useMemo(() => {
    return creditBalances
      .map((balance) => ({
        balance,
        player: playerById.get(balance.player_id),
        latest: ledgerEntries.find((entry) => entry.workspace_id === balance.workspace_id && entry.player_id === balance.player_id) || null,
      }))
      .filter(({ balance }) => reportFilters.includeZeroCredits || Number(balance.credit_balance || 0) !== 0)
      .sort((a, b) => Number(b.balance.credit_balance || 0) - Number(a.balance.credit_balance || 0) || formatPlayerName(a.player, userPreferences).localeCompare(formatPlayerName(b.player, userPreferences)));
  }, [creditBalances, ledgerEntries, playerById, reportFilters.includeZeroCredits, userPreferences]);

  const creditReportTotal = useMemo(() => creditReportRows.reduce((sum, row) => sum + Math.max(0, Number(row.balance.credit_balance || 0)), 0), [creditReportRows]);
  const selectedLedgerEntries = useMemo(() => ledgerEntries.filter((entry) => entry.player_id === ledgerPlayerId), [ledgerEntries, ledgerPlayerId]);
  const selectedLedgerPlayer = ledgerPlayerId ? playerById.get(ledgerPlayerId) : null;
  const importedTransactionByInboundId = useMemo(() => new Map(importedPaymentTransactions.map((transaction) => [transaction.source_inbound_email_id, transaction])), [importedPaymentTransactions]);
  const importedAllocationsByTransactionId = useMemo(() => {
    const map = new Map<string, ImportedPaymentAllocation[]>();
    importedPaymentAllocations.forEach((allocation) => map.set(allocation.imported_payment_transaction_id, [...(map.get(allocation.imported_payment_transaction_id) || []), allocation]));
    return map;
  }, [importedPaymentAllocations]);
  const paymentInboxRows = useMemo(() => inboundPaymentEmails.filter((email) => paymentInboxFilter === 'all' || email.processing_status === paymentInboxFilter), [inboundPaymentEmails, paymentInboxFilter]);
  const paymentAddress = (workspace: WorkspacePaymentSettings) => `payments+${workspace.payment_email_token}@courtcaptain.net`;
  const processingStatusLabel = (status: string) => status.replaceAll('_', ' ');

  const applyCreditAssignment = applyCreditAssignmentId ? assignmentsWithBalance.find((row) => row.id === applyCreditAssignmentId) || null : null;
  const applyCreditWorkspaceId = applyCreditAssignment?.team_id ? workspaceByTeamId.get(applyCreditAssignment.team_id) || '' : '';
  const applyCreditSources = useMemo(() => creditSourcesForWorkspace(applyCreditWorkspaceId), [applyCreditWorkspaceId, creditSourcesForWorkspace]);
  const applyCreditSourcePlayer = applyCreditForm.sourcePlayerId ? playerById.get(applyCreditForm.sourcePlayerId) || null : null;
  const applyCreditAvailable = applyCreditForm.sourcePlayerId ? playerCreditBalance(applyCreditForm.sourcePlayerId, applyCreditWorkspaceId) : 0;
  const paymentAssignment = paymentAssignmentId ? assignmentsWithBalance.find((row) => row.id === paymentAssignmentId) || null : null;
  const paymentCashReceived = Number(paymentForm.amount || 0);
  const paymentAppliedCash = paymentAssignment ? Math.min(Math.max(0, paymentCashReceived), paymentAssignment.outstanding) : 0;
  const paymentOverpayment = paymentAssignment ? Math.max(0, paymentCashReceived - paymentAppliedCash) : 0;

  const resetCreate = () => {
    if (readOnly) return;
    setEditingCampaignId(null);
    setCampaignForm({ fee_name: '', fee_type: 'Court Fees', description: '', amount_per_player: '0', assignment_granularity: 'per_team', due_date: '', notes: '', active_status: true });
    setAssignmentScope('team');
    const firstCaptainTeam = teams.find((team) => defaultCaptainTeamIds.includes(team.id));
    setSelectedTeamIds(firstCaptainTeam ? [firstCaptainTeam.id] : []);
    setSelectedPlayerIds([]);
    setIndividualTeamId('');
    setCreateOpen(true);
  };

  const openEditCampaign = (campaign: FeeCampaign) => {
    if (readOnly) return;
    setEditingCampaignId(campaign.id);
    setCampaignForm({
      fee_name: campaign.fee_name,
      fee_type: campaign.fee_type,
      description: campaign.description || '',
      amount_per_player: String(campaign.amount_per_player),
      assignment_granularity: campaign.assignment_granularity || 'per_team',
      due_date: campaign.due_date || '',
      notes: campaign.notes || '',
      active_status: campaign.active_status,
    });
    setCreateOpen(true);
  };

  const saveCampaign = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (!campaignForm.fee_name.trim()) {
      setSnack({ severity: 'error', message: 'Fee Name is required.' });
      return;
    }
    if (Number(campaignForm.amount_per_player) < 0 || Number.isNaN(Number(campaignForm.amount_per_player))) {
      setSnack({ severity: 'error', message: 'Amount Per Player must be zero or greater.' });
      return;
    }
    const editingCampaign = editingCampaignId ? campaignById.get(editingCampaignId) || null : null;
    if (!editingCampaign && previewAssignments.length === 0) {
      setSnack({ severity: 'error', message: 'Select at least one player assignment.' });
      return;
    }

    const amount = Number(campaignForm.amount_per_player);
    const payload = {
      fee_name: campaignForm.fee_name.trim(),
      fee_type: campaignForm.fee_type,
      description: campaignForm.description || null,
      amount_per_player: amount,
      assignment_granularity: campaignForm.assignment_granularity,
      due_date: campaignForm.due_date || null,
      active_status: campaignForm.active_status,
      notes: campaignForm.notes || null,
      updated_by: auditUserId,
    };

    if (editingCampaign) {
      let amountChoice: string | null = '1';
      if (Number(editingCampaign.amount_per_player) !== amount) {
        amountChoice = window.prompt(
          'Amount Per Player changed. Choose how to update existing assignments:\n\n1. Update future/new assignments only\n2. Update all unpaid assignments\n3. Leave existing assignments unchanged',
          '1',
        );
        if (amountChoice === null) return;
        if (!['1', '2', '3'].includes(amountChoice.trim())) {
          setSnack({ severity: 'error', message: 'Choose 1, 2, or 3 for the amount update.' });
          return;
        }
      }

      const { error: updateError } = await supabase.from('team_fee_campaigns').update(payload).eq('id', editingCampaign.id);
      if (updateError) {
        setSnack({ severity: 'error', message: updateError.message });
        return;
      }

      if (amountChoice?.trim() === '2') {
        const { error: assignmentUpdateError } = await supabase
          .from('player_fee_assignments')
          .update({ amount_owed: amount, payment_status: 'Unpaid', updated_by: auditUserId })
          .eq('fee_campaign_id', editingCampaign.id)
          .eq('payment_status', 'Unpaid')
          .is('deleted_at', null);
        if (assignmentUpdateError) {
          setSnack({ severity: 'error', message: assignmentUpdateError.message });
          return;
        }
      }

      setCreateOpen(false);
      setEditingCampaignId(null);
      setSelectedCampaignId(editingCampaign.id);
      setSnack({ severity: 'success', message: 'Fee campaign updated.' });
      await loadData();
      return;
    }

    const { data: campaign, error: campaignError } = await supabase
      .from('team_fee_campaigns')
      .insert({
        ...payload,
        created_by: auditUserId,
      })
      .select('id')
      .single();

    if (campaignError || !campaign) {
      setSnack({ severity: 'error', message: campaignError?.message || 'Unable to save fee campaign.' });
      return;
    }

    const { error: assignmentError } = await supabase.from('player_fee_assignments').insert(
      previewAssignments.map((assignment) => ({
        fee_campaign_id: campaign.id,
        organization_id: assignment.organization_id,
        team_id: assignment.team_id,
        player_id: assignment.player_id,
        amount_owed: assignment.amount_owed,
        payment_status: 'Unpaid',
        created_by: auditUserId,
        updated_by: auditUserId,
      })),
    );

    if (assignmentError) {
      setSnack({ severity: 'error', message: assignmentError.message });
      return;
    }

    setCreateOpen(false);
    setSelectedCampaignId(campaign.id);
    setSnack({ severity: 'success', message: 'Fee campaign saved.' });
    await loadData();
  };

  const softDeleteAssignment = async (assignmentId: string) => {
    if (readOnly) return;
    const { error } = await supabase.from('player_fee_assignments').update({ deleted_at: new Date().toISOString(), delete_reason: 'Removed from Fees & Payments screen', updated_by: auditUserId }).eq('id', assignmentId);
    if (error) setSnack({ severity: 'error', message: error.message });
    else {
      setSnack({ severity: 'success', message: 'Player removed from fee campaign.' });
      await loadData();
    }
  };

  const updateAssignment = async (assignmentId: string, patch: Partial<FeeAssignment>) => {
    if (readOnly) return;
    const { error } = await supabase.from('player_fee_assignments').update({ ...patch, updated_by: auditUserId }).eq('id', assignmentId);
    if (error) setSnack({ severity: 'error', message: error.message });
    else await loadData();
  };

  const editAmount = async (assignment: AssignmentWithBalance) => {
    if (readOnly) return;
    const nextAmount = window.prompt('Amount owed', String(assignment.amount_owed));
    if (nextAmount === null) return;
    const amount = Number(nextAmount);
    if (Number.isNaN(amount) || amount < 0) {
      setSnack({ severity: 'error', message: 'Amount owed must be zero or greater.' });
      return;
    }
    await updateAssignment(assignment.id, { amount_owed: amount, payment_status: assignment.payment_status === 'Waived' ? 'Waived' : computedStatus({ ...assignment, amount_owed: amount }, assignment.amount_paid) });
  };

  const savePayment = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    const assignment = assignmentsWithBalance.find((row) => row.id === paymentAssignmentId);
    if (!assignment) return;
    const amount = Number(paymentForm.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      setSnack({ severity: 'error', message: 'Payment amount must be greater than zero.' });
      return;
    }
    const { error } = await supabase.rpc('record_fee_payment_with_credit', {
      p_fee_assignment_id: assignment.id,
      p_cash_received: amount,
      p_payment_date: paymentForm.payment_date,
      p_payment_method: paymentForm.payment_method,
      p_notes: paymentForm.notes || null,
      p_overpayment_action: paymentForm.overpayment_action,
      p_return_overpayment_amount: paymentForm.overpayment_action === 'return' ? Math.max(0, amount - assignment.outstanding) : 0,
      p_created_by: auditUserId,
      p_idempotency_key: `payment:${assignment.id}:${Date.now()}`,
    });
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setPaymentAssignmentId(null);
    setPaymentForm({ amount: '', payment_date: today(), payment_method: 'Venmo', notes: '', overpayment_action: 'retain_credit' });
    setSnack({ severity: 'success', message: paymentOverpayment > 0 && paymentForm.overpayment_action === 'retain_credit' ? 'Payment recorded and overpayment retained as player credit.' : 'Payment recorded.' });
    await loadData();
  };

  const saveApplyCredit = async () => {
    if (readOnly) return;
    const assignment = applyCreditAssignment;
    if (!assignment) return;
    const amount = Number(applyCreditForm.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      setSnack({ severity: 'error', message: 'Credit amount must be greater than zero.' });
      return;
    }
    if (!applyCreditForm.sourcePlayerId) {
      setSnack({ severity: 'error', message: 'Select the player whose credit should be used.' });
      return;
    }
    const rpcArgs = {
      p_fee_assignment_id: assignment.id,
      p_amount: amount,
      p_description: applyCreditForm.description || null,
      p_created_by: auditUserId,
      p_idempotency_key: `apply-credit:${assignment.id}:${applyCreditForm.sourcePlayerId}:${Date.now()}`,
    };
    const { error } = applyCreditForm.sourcePlayerId === assignment.player_id
      ? await supabase.rpc('apply_player_credit_to_fee_assignment', rpcArgs)
      : await supabase.rpc('apply_player_credit_to_other_fee_assignment', {
        ...rpcArgs,
        p_source_player_id: applyCreditForm.sourcePlayerId,
      });
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setApplyCreditAssignmentId(null);
    setApplyCreditDescriptionEdited(false);
    setApplyCreditForm({ sourcePlayerId: '', amount: '', description: '' });
    setSnack({ severity: 'success', message: 'Player credit applied.' });
    await loadData();
  };

  const saveCreditAdjustment = async () => {
    if (readOnly) return;
    const amount = Number(creditAdjustmentForm.amount);
    if (!creditAdjustmentForm.workspaceId || !creditAdjustmentForm.playerId) {
      setSnack({ severity: 'error', message: 'Workspace and player are required.' });
      return;
    }
    if (Number.isNaN(amount) || amount <= 0) {
      setSnack({ severity: 'error', message: 'Amount must be greater than zero.' });
      return;
    }
    if (!creditAdjustmentForm.description.trim()) {
      setSnack({ severity: 'error', message: 'A reason is required.' });
      return;
    }
    if (!window.confirm('This will create an immutable ledger entry. Continue?')) return;
    const { error } = await supabase.rpc('create_player_credit_adjustment', {
      p_workspace_id: creditAdjustmentForm.workspaceId,
      p_player_id: creditAdjustmentForm.playerId,
      p_transaction_type: creditAdjustmentForm.transactionType,
      p_amount: amount,
      p_description: creditAdjustmentForm.description.trim(),
      p_created_by: auditUserId,
      p_related_ledger_entry_id: null,
      p_idempotency_key: `credit-adjustment:${creditAdjustmentForm.playerId}:${Date.now()}`,
    });
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setCreditAdjustmentOpen(false);
    setCreditAdjustmentForm({ workspaceId: '', playerId: '', transactionType: 'manual_credit', amount: '', description: '' });
    setSnack({ severity: 'success', message: 'Player credit ledger updated.' });
    await loadData();
  };

  const openAddAssignment = (campaign: FeeCampaign) => {
    if (readOnly) return;
    if (!campaign.active_status) {
      setSnack({ severity: 'error', message: 'Archived fee campaigns cannot receive new assignments.' });
      return;
    }
    setAddAssignmentCampaignId(campaign.id);
    setAddAssignmentForm({ teamId: '', playerIds: [], amount: String(campaign.amount_per_player) });
  };

  const saveAddedAssignments = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    const campaign = addAssignmentCampaignId ? campaignById.get(addAssignmentCampaignId) : null;
    if (!campaign) return;
    const amount = Number(addAssignmentForm.amount);
    if (Number.isNaN(amount) || amount < 0) {
      setSnack({ severity: 'error', message: 'Amount owed must be zero or greater.' });
      return;
    }
    if (addAssignmentForm.playerIds.length === 0) {
      setSnack({ severity: 'error', message: 'Select at least one player.' });
      return;
    }
    if (!campaign.active_status) {
      setSnack({ severity: 'error', message: 'Archived fee campaigns cannot receive new assignments.' });
      return;
    }
    const existingKeys = new Set(assignments.filter((assignment) => assignment.fee_campaign_id === campaign.id).map((assignment) => `${assignment.team_id || ''}:${assignment.player_id}`));
    const existingPlayerIds = new Set(assignments.filter((assignment) => assignment.fee_campaign_id === campaign.id).map((assignment) => assignment.player_id));
    const seenNewKeys = new Set<string>();
    const newAssignments = addAssignmentForm.playerIds.flatMap((playerId) => {
      if ((campaign.assignment_granularity || 'per_team') === 'per_player' && existingPlayerIds.has(playerId)) return [];
      const teamIds = addAssignmentForm.teamId
        ? [addAssignmentForm.teamId]
        : rosters.filter((roster) => roster.player_id === playerId).map((roster) => roster.team_id);
      const scopedTeamIds = (campaign.assignment_granularity || 'per_team') === 'per_player' ? teamIds.slice(0, 1) : teamIds;
      return scopedTeamIds.flatMap((teamId) => {
        const team = teamById.get(teamId);
        const key = `${teamId}:${playerId}`;
        if (!team || existingKeys.has(key) || seenNewKeys.has(key)) return [];
        seenNewKeys.add(key);
        return [{
          key,
          organization_id: team.organization_id || null,
          team_id: teamId,
          player_id: playerId,
        }];
      });
    });
    if (newAssignments.length === 0) {
      setSnack({ severity: 'success', message: 'Selected players already have assignments for this campaign and team context.' });
      setAddAssignmentCampaignId(null);
      return;
    }
    const { error } = await supabase.from('player_fee_assignments').insert(
      newAssignments.map((assignment) => ({
        fee_campaign_id: campaign.id,
        organization_id: assignment.organization_id,
        team_id: assignment.team_id,
        player_id: assignment.player_id,
        amount_owed: amount,
        payment_status: 'Unpaid',
        created_by: auditUserId,
        updated_by: auditUserId,
      })),
    );
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setAddAssignmentCampaignId(null);
    setSnack({ severity: 'success', message: `${newAssignments.length} player assignment${newAssignments.length === 1 ? '' : 's'} added.` });
    await loadData();
  };

  const setCampaignActiveStatus = async (campaign: FeeCampaign, activeStatus: boolean) => {
    if (readOnly) return;
    const { error } = await supabase
      .from('team_fee_campaigns')
      .update({ active_status: activeStatus, updated_by: auditUserId })
      .eq('id', campaign.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: activeStatus ? 'Fee campaign reopened.' : 'Fee campaign archived.' });
    await loadData();
  };

  const syncSelectedCampaignAssignments = async () => {
    if (readOnly) return;
    if (!selectedCampaign || selectedCampaignCoverage.length === 0) return;
    let createdCount = 0;
    try {
      for (const coverage of selectedCampaignCoverage) {
        createdCount += await syncTeamFeeAssignments(coverage.teamId, undefined, selectedCampaign.id, auditUserId);
      }
    } catch (syncError) {
      setSnack({ severity: 'error', message: syncError instanceof Error ? syncError.message : 'Unable to sync fee assignments.' });
      return;
    }
    setSnack({ severity: 'success', message: `${createdCount} missing fee assignment${createdCount === 1 ? '' : 's'} created.` });
    await loadData();
  };

  const copyOutstanding = async () => {
    const lines = ['Outstanding Fees', ''];
    outstandingRows.forEach(({ assignment, campaign }) => {
      const player = formatPlayerName(playerById.get(assignment.player_id), userPreferences);
      const team = optionLabel(lookups.teams, assignment.team_id);
      const overdue = daysOverdue(campaign.due_date, assignment.computed_status) > 0;
      lines.push(`${player}${team ? ` - ${team}` : ''}`);
      lines.push(`${campaign.fee_name}: ${money(assignment.outstanding)} ${overdue ? `overdue since ${dueLabel(campaign.due_date)}` : campaign.due_date ? `due ${dueLabel(campaign.due_date)}` : 'due date not set'}`);
      lines.push('');
    });
    await navigator.clipboard.writeText(lines.join('\n').trim());
    setSnack({ severity: 'success', message: 'Outstanding fees copied.' });
  };

  const copyPaymentAddress = async (workspace: WorkspacePaymentSettings) => {
    await navigator.clipboard.writeText(paymentAddress(workspace));
    setSnack({ severity: 'success', message: 'Payment forwarding address copied.' });
  };

  const updatePaymentImportMode = async (workspaceId: string, mode: WorkspacePaymentSettings['payment_email_import_mode']) => {
    if (readOnly) return;
    const { error } = await supabase.from('workspaces').update({ payment_email_import_mode: mode }).eq('id', workspaceId);
    if (error) setSnack({ severity: 'error', message: error.message });
    else {
      setSnack({ severity: 'success', message: 'Automatic payment email import setting updated.' });
      await loadData();
    }
  };

  const regeneratePaymentAddress = async (workspace: WorkspacePaymentSettings) => {
    if (readOnly) return;
    if (!window.confirm(`Regenerate the payment forwarding address for ${workspace.name}? The old address will stop working immediately.`)) return;
    const { error } = await supabase.rpc('regenerate_workspace_payment_email_token', { p_workspace_id: workspace.id });
    if (error) setSnack({ severity: 'error', message: error.message });
    else {
      setSnack({ severity: 'success', message: 'Payment forwarding address regenerated.' });
      await loadData();
    }
  };

  const applyInboundPayment = async (email: InboundPaymentEmail) => {
    if (readOnly) return;
    const playerId = manualMatchByEmailId[email.id] || email.matched_player_id;
    if (!playerId) {
      setSnack({ severity: 'error', message: 'Select a player before applying this payment.' });
      return;
    }
    if (!window.confirm(`Apply ${money(Number(email.parsed_amount || 0))} from ${email.parsed_payer_name || 'this payer'} to ${formatPlayerName(playerById.get(playerId), userPreferences)}?`)) return;
    const { error } = await supabase.rpc('apply_imported_payment_to_player', {
      p_inbound_email_id: email.id,
      p_player_id: playerId,
      p_actor_id: auditUserId,
      p_manual: true,
    });
    if (error) setSnack({ severity: 'error', message: error.message });
    else {
      setSnack({ severity: 'success', message: 'Imported payment applied.' });
      await loadData();
    }
  };

  const retryPaymentEmailParse = async (email: InboundPaymentEmail, recoverIgnored = false) => {
    if (readOnly) return;
    if (recoverIgnored && (!canManageEmailImport || !window.confirm(`Reparse this ignored payment email and apply it if it matches a player?\n\n${email.subject || 'No subject'}\n${email.from_header || 'Unknown sender'}\n\nCheck that it has not already been entered manually before continuing.`))) return;
    const session = (await supabase.auth.getSession()).data.session;
    const response = await fetch('/api/payment-emails/retry-parse', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ inboundEmailId: email.id, recoverIgnored }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok) {
      setSnack({ severity: 'error', message: result.error || 'Unable to retry payment email parsing.' });
      return;
    }
    const status = result.result?.processing_status || result.result?.[0]?.processing_status || 'reprocessed';
    setSnack({ severity: 'success', message: `Payment email parse retried: ${processingStatusLabel(status)}.` });
    await loadData();
  };

  const markInboundPayment = async (email: InboundPaymentEmail, status: 'duplicate' | 'ignored') => {
    if (readOnly) return;
    if (status === 'ignored' && looksLikeInboundPayment(email)
      && !window.confirm('This looks like a payment notification. Ignoring it will leave any fee unpaid. Continue?')) return;
    const reason = window.prompt(`Reason to mark this payment email as ${status}:`, status === 'duplicate' ? 'Duplicate notification' : 'Not a payment to import');
    if (reason === null) return;
    const { error } = await supabase.rpc('mark_inbound_payment_email_status', {
      p_inbound_email_id: email.id,
      p_processing_status: status,
      p_reason: reason || status,
      p_actor_id: auditUserId,
    });
    if (error) setSnack({ severity: 'error', message: error.message });
    else {
      setSnack({ severity: 'success', message: `Payment email marked ${status}.` });
      await loadData();
    }
  };

  const reverseImportedPayment = async (transaction: ImportedPaymentTransaction) => {
    if (readOnly) return;
    const reason = window.prompt('Why are you reversing this imported payment?');
    if (!reason?.trim()) return;
    const { error } = await supabase.rpc('reverse_imported_payment_transaction', {
      p_imported_payment_transaction_id: transaction.id,
      p_reason: reason.trim(),
      p_actor_id: auditUserId,
    });
    if (error) setSnack({ severity: 'error', message: error.message });
    else {
      setSnack({ severity: 'success', message: 'Imported payment reversed.' });
      await loadData();
    }
  };

  const savePaymentAlias = async () => {
    if (readOnly) return;
    if (!aliasForm.workspaceId || !aliasForm.playerId || !aliasForm.alias.trim()) {
      setSnack({ severity: 'error', message: 'Workspace, player, and alias are required.' });
      return;
    }
    const { error } = await supabase.from('player_payment_aliases').insert({
      workspace_id: aliasForm.workspaceId,
      player_id: aliasForm.playerId,
      provider: aliasForm.provider === 'Any' ? null : aliasForm.provider,
      alias_display_name: aliasForm.alias.trim(),
      normalized_alias: aliasForm.alias.trim().replace(/\s+/g, ' ').toLowerCase(),
      is_verified: true,
      is_active: true,
      created_by: auditUserId,
    });
    if (error) setSnack({ severity: 'error', message: error.message });
    else {
      setAliasForm({ workspaceId: '', playerId: '', provider: 'Venmo', alias: '' });
      setSnack({ severity: 'success', message: 'Verified payment alias added.' });
      await loadData();
    }
  };

  const deactivatePaymentAlias = async (alias: PlayerPaymentAlias) => {
    if (readOnly) return;
    const { error } = await supabase.from('player_payment_aliases').update({ is_active: false, deactivated_at: new Date().toISOString(), deactivated_by: auditUserId }).eq('id', alias.id);
    if (error) setSnack({ severity: 'error', message: error.message });
    else await loadData();
  };

  const selectedHistory = paymentsByAssignment.get(historyAssignmentId || '') || [];

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Fees & Payments</Typography>
          <Typography variant="body2" color="text.secondary">Track team fees, player balances, partial payments, and overdue amounts.</Typography>
        </Box>
        {!readOnly && <Button variant="contained" startIcon={<AddIcon />} onClick={resetCreate} sx={{ width: { xs: '100%', sm: 'auto' } }}>Create Fee Campaign</Button>}
      </Box>

      <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
        <Tab value="campaigns" label="Fee Campaigns" />
        <Tab value="outstanding" label="Outstanding Fees" />
        <Tab value="credits" label="Player Credits" />
        <Tab value="payment-inbox" label="Payment Inbox" />
        {canManageEmailImport && <Tab value="payment-import-settings" label="Email Import Settings" />}
      </Tabs>

      {loading && <LinearProgress />}
      {tab === 'outstanding' && shouldShowCaptainScopeEmpty(reportFilters.teamIds, defaultCaptainTeamIds, teamScopeMode) && <Alert severity="info">You aren't assigned as captain or co-captain of a team. Select a team to view its outstanding fees.</Alert>}

      {tab === 'campaigns' ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth><InputLabel>Fee Type</InputLabel><Select label="Fee Type" value={campaignFilters.feeType} onChange={(event) => setCampaignFilters((current) => ({ ...current, feeType: event.target.value }))}><MenuItem value="all">All Types</MenuItem>{feeTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Active Status</InputLabel><Select label="Active Status" value={campaignFilters.activeStatus} onChange={(event) => setCampaignFilters((current) => ({ ...current, activeStatus: event.target.value }))}><MenuItem value="all">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem></Select></FormControl>
              <TextField label="Due From" type="date" value={campaignFilters.dueFrom} InputLabelProps={{ shrink: true }} onChange={(event) => setCampaignFilters((current) => ({ ...current, dueFrom: event.target.value }))} />
              <TextField label="Due To" type="date" value={campaignFilters.dueTo} InputLabelProps={{ shrink: true }} onChange={(event) => setCampaignFilters((current) => ({ ...current, dueTo: event.target.value }))} />
            </Box>
          </Paper>

          <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
            {filteredCampaignSummaries.map(({ campaign, teamCount, playerCount, totalOwed, totalCashPaid, totalCreditApplied, totalOutstanding }) => (
              <Paper key={campaign.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{campaign.fee_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{campaign.fee_type} • Due {campaign.due_date || 'not set'} • {campaign.active_status ? 'Active' : 'Inactive'}</Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                    <Box><Typography variant="caption" color="text.secondary">Teams</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{teamCount}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">Players</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{playerCount}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">Owed</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(totalOwed)}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">Outstanding</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(totalOutstanding)}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">Cash Paid</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(totalCashPaid)}</Typography></Box>
                    <Box><Typography variant="caption" color="text.secondary">Credit Applied</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(totalCreditApplied)}</Typography></Box>
                  </Box>
                  <Stack direction="column" spacing={0.75}>
                    <Button size="small" variant="contained" startIcon={<VisibilityIcon />} onClick={() => setSelectedCampaignId(campaign.id)}>Open</Button>
                    {!readOnly && (
                      <>
                        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditCampaign(campaign)}>Edit</Button>
                        {campaign.active_status ? (
                          <Button size="small" variant="outlined" startIcon={<ArchiveIcon />} onClick={() => void setCampaignActiveStatus(campaign, false)}>Archive</Button>
                        ) : (
                          <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={() => void setCampaignActiveStatus(campaign, true)}>Reopen</Button>
                        )}
                      </>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ))}
            {filteredCampaignSummaries.length === 0 && !loading && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No fee campaigns match the current filters.</Typography>
            )}
          </Box>

          <Paper variant="outlined" sx={{ overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 980 }}>
                <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Fee Name</TableCell><TableCell sx={{ fontWeight: 700 }}>Fee Type</TableCell><TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Teams</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Players</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Total Owed</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Cash Paid</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Credit Applied</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Outstanding</TableCell><TableCell sx={{ fontWeight: 700 }}>Status</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell></TableRow></TableHead>
                <TableBody>
                  {filteredCampaignSummaries.map(({ campaign, teamCount, playerCount, totalOwed, totalCashPaid, totalCreditApplied, totalOutstanding }) => (
                    <TableRow key={campaign.id} hover>
                      <TableCell>{campaign.fee_name}</TableCell><TableCell>{campaign.fee_type}</TableCell><TableCell>{campaign.due_date || '-'}</TableCell><TableCell align="right">{teamCount}</TableCell><TableCell align="right">{playerCount}</TableCell><TableCell align="right">{money(totalOwed)}</TableCell><TableCell align="right">{money(totalCashPaid)}</TableCell><TableCell align="right">{money(totalCreditApplied)}</TableCell><TableCell align="right">{money(totalOutstanding)}</TableCell><TableCell>{campaign.active_status ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                          <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setSelectedCampaignId(campaign.id)}>Open</Button>
                          {!readOnly && (
                            <>
                              <Button size="small" startIcon={<EditIcon />} onClick={() => openEditCampaign(campaign)}>Edit</Button>
                              {campaign.active_status ? (
                                <Button size="small" startIcon={<ArchiveIcon />} onClick={() => void setCampaignActiveStatus(campaign, false)}>Archive</Button>
                              ) : (
                                <Button size="small" startIcon={<ReplayIcon />} onClick={() => void setCampaignActiveStatus(campaign, true)}>Reopen</Button>
                              )}
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredCampaignSummaries.length === 0 && !loading && <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No fee campaigns match the current filters.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {selectedCampaign && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                  <Box><Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedCampaign.fee_name}</Typography><Typography variant="body2" color="text.secondary">{selectedCampaign.description || 'No description.'}</Typography></Box>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    {!readOnly && (
                      <>
                        <Button startIcon={<EditIcon />} onClick={() => openEditCampaign(selectedCampaign)}>Edit Campaign</Button>
                        <Button startIcon={<SyncIcon />} disabled={!selectedCampaign.active_status || selectedCampaignCoverage.length === 0} onClick={() => void syncSelectedCampaignAssignments()}>Sync Team Assignments</Button>
                        <Button startIcon={<AddIcon />} disabled={!selectedCampaign.active_status} onClick={() => openAddAssignment(selectedCampaign)}>Add Player</Button>
                        {selectedCampaign.active_status ? (
                          <Button startIcon={<ArchiveIcon />} onClick={() => void setCampaignActiveStatus(selectedCampaign, false)}>Archive</Button>
                        ) : (
                          <Button startIcon={<ReplayIcon />} onClick={() => void setCampaignActiveStatus(selectedCampaign, true)}>Reopen</Button>
                        )}
                      </>
                    )}
                    <Button onClick={() => setSelectedCampaignId(null)}>Close</Button>
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Assignment Coverage</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 520 }}>
                      <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Team</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Roster Players</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Assigned Players</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Missing Assignments</TableCell></TableRow></TableHead>
                      <TableBody>
                        {selectedCampaignCoverage.map((coverage) => (
                          <TableRow key={coverage.teamId} hover>
                            <TableCell>{optionLabel(lookups.teams, coverage.teamId)}</TableCell>
                            <TableCell align="right">{coverage.rosterPlayers}</TableCell>
                            <TableCell align="right">{coverage.assignedPlayers}</TableCell>
                            <TableCell align="right">{coverage.missingAssignments}</TableCell>
                          </TableRow>
                        ))}
                        {selectedCampaignCoverage.length === 0 && (
                          <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No team-based assignments for this campaign yet.</Typography></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
                  {selectedCampaignAssignments.map((assignment) => (
                    <Paper key={assignment.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatPlayerName(playerById.get(assignment.player_id), userPreferences)}</Typography>
                          <Typography variant="body2" color="text.secondary">{optionLabel(lookups.teams, assignment.team_id)} • {optionLabel(lookups.organizations, assignment.organization_id)}</Typography>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                          <Box><Typography variant="caption" color="text.secondary">Amount Owed</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(assignment.amount_owed)}</Typography></Box>
                          <Box><Typography variant="caption" color="text.secondary">Outstanding</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(assignment.outstanding)}</Typography></Box>
                          <Box><Typography variant="caption" color="text.secondary">Cash Paid</Typography><Typography variant="body2">{money(assignment.cash_paid)}</Typography></Box>
                          <Box><Typography variant="caption" color="text.secondary">Credit Applied</Typography><Typography variant="body2">{money(assignment.credit_applied)}</Typography></Box>
                          <Box><Typography variant="caption" color="text.secondary">Status</Typography><Typography variant="body2">{assignment.computed_status}</Typography></Box>
                          <Box><Typography variant="caption" color="text.secondary">Method</Typography><Typography variant="body2">{assignment.latest_payment_method || '-'}</Typography></Box>
                          <Box><Typography variant="caption" color="text.secondary">Paid Date</Typography><Typography variant="body2">{assignment.latest_paid_date || '-'}</Typography></Box>
                        </Box>
                        {renderAssignmentActions(assignment, 'column')}
                      </Stack>
                    </Paper>
                  ))}
                </Box>
                <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
                  <Table size="small" sx={{ minWidth: 1050 }}>
                    <TableHead>
                      <TableRow>
                        {campaignAssignmentSortCell('player', 'Player')}
                        {campaignAssignmentSortCell('organization', 'Organization')}
                        {campaignAssignmentSortCell('team', 'Team')}
                        {campaignAssignmentSortCell('amount_owed', 'Amount Owed', 'right')}
                        {campaignAssignmentSortCell('cash_paid', 'Cash Paid', 'right')}
                        {campaignAssignmentSortCell('credit_applied', 'Credit Applied', 'right')}
                        {campaignAssignmentSortCell('outstanding', 'Outstanding', 'right')}
                        {campaignAssignmentSortCell('status', 'Status')}
                        {campaignAssignmentSortCell('method', 'Method')}
                        {campaignAssignmentSortCell('paid_date', 'Paid Date')}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedCampaignAssignments.map((assignment) => (
                        <TableRow key={assignment.id} hover>
                          <TableCell>{formatPlayerName(playerById.get(assignment.player_id), userPreferences)}</TableCell>
                          <TableCell>{optionLabel(lookups.organizations, assignment.organization_id)}</TableCell>
                          <TableCell>{optionLabel(lookups.teams, assignment.team_id)}</TableCell>
                          <TableCell align="right">{money(assignment.amount_owed)}</TableCell>
                          <TableCell align="right">{money(assignment.cash_paid)}</TableCell>
                          <TableCell align="right">{money(assignment.credit_applied)}</TableCell>
                          <TableCell align="right">{money(assignment.outstanding)}</TableCell>
                          <TableCell>{assignment.computed_status}</TableCell>
                          <TableCell>{assignment.latest_payment_method || '-'}</TableCell>
                          <TableCell>{assignment.latest_paid_date || '-'}</TableCell>
                          <TableCell align="right">
                            {renderAssignmentActions(assignment)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Paper>
          )}
        </Stack>
      ) : tab === 'outstanding' ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth><InputLabel>Organization</InputLabel><Select label="Organization" value={reportFilters.organizationId} onChange={(event) => setReportFilters((current) => ({ ...current, organizationId: event.target.value }))}><MenuItem value="all">All Organizations</MenuItem>{lookups.organizations.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
              <SeasonTeamMultiSelect lookups={lookups} value={reportFilters.teamIds} onChange={(teamIds) => { setReportFilters((current) => ({ ...current, teamIds })); setTeamScopeMode('explicit'); }} allLabel={teamScopeMode === 'default' && defaultCaptainTeamIds.length === 0 ? 'Select a team' : 'All Teams'} />
              <FormControl fullWidth><InputLabel>Player</InputLabel><Select label="Player" value={reportFilters.playerId} onChange={(event) => setReportFilters((current) => ({ ...current, playerId: event.target.value }))}><MenuItem value="all">All Players</MenuItem>{lookups.players.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Payment Status</InputLabel><Select label="Payment Status" value={reportFilters.status} onChange={(event) => setReportFilters((current) => ({ ...current, status: event.target.value }))}><MenuItem value="all">All Open</MenuItem>{paymentStatusOptions.filter((option) => !['Paid', 'Waived'].includes(option.value)).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Fee Type</InputLabel><Select label="Fee Type" value={reportFilters.feeType} onChange={(event) => setReportFilters((current) => ({ ...current, feeType: event.target.value }))}><MenuItem value="all">All Types</MenuItem>{feeTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Fee Campaign</InputLabel><Select label="Fee Campaign" value={reportFilters.campaignId} onChange={(event) => setReportFilters((current) => ({ ...current, campaignId: event.target.value }))}><MenuItem value="all">All Campaigns</MenuItem>{campaigns.map((campaign) => <MenuItem key={campaign.id} value={campaign.id}>{campaign.fee_name}</MenuItem>)}</Select></FormControl>
              <TextField label="Due From" type="date" value={reportFilters.dueFrom} InputLabelProps={{ shrink: true }} onChange={(event) => setReportFilters((current) => ({ ...current, dueFrom: event.target.value }))} />
              <TextField label="Due To" type="date" value={reportFilters.dueTo} InputLabelProps={{ shrink: true }} onChange={(event) => setReportFilters((current) => ({ ...current, dueTo: event.target.value }))} />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
              <FormControlLabel control={<Checkbox checked={reportFilters.overdueOnly} onChange={(event) => setReportFilters((current) => ({ ...current, overdueOnly: event.target.checked }))} />} label="Overdue only" />
              <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={() => void copyOutstanding()}>Copy Outstanding Fees</Button>
            </Stack>
          </Paper>
          <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
            {outstandingRows.map(({ assignment, campaign }) => {
              const overdueDays = daysOverdue(campaign.due_date, assignment.computed_status);
              return (
                <Paper key={assignment.id} variant="outlined" sx={{ p: 1.5, bgcolor: overdueDays > 0 ? '#fff1f0' : 'background.paper' }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatPlayerName(playerById.get(assignment.player_id), userPreferences)}</Typography>
                      <Typography variant="body2" color="text.secondary">{optionLabel(lookups.teams, assignment.team_id)} • {campaign.fee_name}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                      <Box><Typography variant="caption" color="text.secondary">Outstanding</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{money(assignment.outstanding)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Status</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{assignment.computed_status}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Owed</Typography><Typography variant="body2">{money(assignment.amount_owed)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Paid</Typography><Typography variant="body2">{money(assignment.amount_paid)}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Due Date</Typography><Typography variant="body2">{campaign.due_date || '-'}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Days Overdue</Typography><Typography variant="body2">{overdueDays}</Typography></Box>
                    </Box>
                    {renderAssignmentActions(assignment, 'column')}
                  </Stack>
                </Paper>
              );
            })}
            {outstandingRows.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No outstanding fees match the current filters.</Typography>}
          </Box>
          <Paper variant="outlined" sx={{ overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ overflowX: 'auto' }}><Table size="small" sx={{ minWidth: 1280 }}><TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Player</TableCell><TableCell sx={{ fontWeight: 700 }}>Organization</TableCell><TableCell sx={{ fontWeight: 700 }}>Team</TableCell><TableCell sx={{ fontWeight: 700 }}>Fee Campaign</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Owed</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Outstanding</TableCell><TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Days Overdue</TableCell><TableCell sx={{ fontWeight: 700 }}>Status</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell></TableRow></TableHead><TableBody>
              {outstandingRows.map(({ assignment, campaign }) => {
                const overdueDays = daysOverdue(campaign.due_date, assignment.computed_status);
                return (
                  <TableRow key={assignment.id} hover sx={overdueDays > 0 ? { bgcolor: '#fff1f0' } : undefined}>
                    <TableCell>{formatPlayerName(playerById.get(assignment.player_id), userPreferences)}</TableCell><TableCell>{optionLabel(lookups.organizations, assignment.organization_id)}</TableCell><TableCell>{optionLabel(lookups.teams, assignment.team_id)}</TableCell><TableCell>{campaign.fee_name}</TableCell><TableCell align="right">{money(assignment.amount_owed)}</TableCell><TableCell align="right">{money(assignment.amount_paid)}</TableCell><TableCell align="right">{money(assignment.outstanding)}</TableCell><TableCell>{campaign.due_date || '-'}</TableCell><TableCell align="right">{overdueDays}</TableCell><TableCell>{assignment.computed_status}</TableCell><TableCell align="right">{renderAssignmentActions(assignment)}</TableCell>
                  </TableRow>
                );
              })}
              {outstandingRows.length === 0 && <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No outstanding fees match the current filters.</Typography></TableCell></TableRow>}
            </TableBody></Table></TableContainer>
          </Paper>
        </Stack>
      ) : tab === 'credits' ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, flex: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Players with Credit</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{creditReportRows.filter((row) => Number(row.balance.credit_balance || 0) > 0).length}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Player Credit Liability</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{money(creditReportTotal)}</Typography>
                </Box>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControlLabel control={<Checkbox checked={reportFilters.includeZeroCredits} onChange={(event) => setReportFilters((current) => ({ ...current, includeZeroCredits: event.target.checked }))} />} label="Include zero balances" />
                {!readOnly && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreditAdjustmentOpen(true)}>Manual Adjustment</Button>}
              </Stack>
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Workspace</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Current Credit Balance</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Most Recent Credit Activity</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creditReportRows.map(({ balance, player, latest }) => (
                    <TableRow key={`${balance.workspace_id}:${balance.player_id}`} hover>
                      <TableCell>{formatPlayerName(player, userPreferences) || optionLabel(lookups.players, balance.player_id)}</TableCell>
                      <TableCell>{optionLabel(lookups.workspaces, balance.workspace_id)}</TableCell>
                      <TableCell align="right"><Chip size="small" color={Number(balance.credit_balance || 0) > 0 ? 'success' : 'default'} label={money(Number(balance.credit_balance || 0))} /></TableCell>
                      <TableCell>{latest ? new Date(latest.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell>{latest?.description || '-'}</TableCell>
                      <TableCell align="right"><Button size="small" startIcon={<HistoryIcon />} onClick={() => setLedgerPlayerId(balance.player_id)}>View Ledger</Button></TableCell>
                    </TableRow>
                  ))}
                  {creditReportRows.length === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No players have credit balances.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      ) : tab === 'payment-inbox' ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Payment Inbox</Typography>
                <Typography variant="body2" color="text.secondary">Review Venmo and Zelle payment notifications imported from forwarded emails.</Typography>
              </Box>
              <FormControl sx={{ minWidth: 240 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select label="Status Filter" value={paymentInboxFilter} onChange={(event) => setPaymentInboxFilter(event.target.value as PaymentInboxFilter)}>
                  <MenuItem value="needs_review">Needs Review</MenuItem>
                  <MenuItem value="automatically_applied">Automatically Applied</MenuItem>
                  <MenuItem value="unmatched">Unmatched</MenuItem>
                  <MenuItem value="duplicate">Duplicate</MenuItem>
                  <MenuItem value="parse_failed">Parse Failed</MenuItem>
                  <MenuItem value="ignored">Ignored</MenuItem>
                  <MenuItem value="reversed">Reversed</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Paper>
          {inboundPaymentEmails.some(isRecoverableIgnoredPayment) && (
            <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => setPaymentInboxFilter('ignored')}>Review Ignored</Button>}>
              {inboundPaymentEmails.filter(isRecoverableIgnoredPayment).length} ignored payment-looking email(s) could not be parsed. Review before assuming the payment was not received.
            </Alert>
          )}
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1320 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payer</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Received</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Matched Player</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Confidence</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Allocations / Credit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Warnings / Error</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentInboxRows.map((email) => {
                    const transaction = importedTransactionByInboundId.get(email.id);
                    const allocations = transaction ? importedAllocationsByTransactionId.get(transaction.id) || [] : [];
                    const selectedPlayerId = manualMatchByEmailId[email.id] || email.matched_player_id || '';
                    return (
                      <TableRow key={email.id} hover sx={{ verticalAlign: 'top' }}>
                        <TableCell>{email.provider || '-'}</TableCell>
                        <TableCell>
                          {email.parsed_payer_name || '-'}
                          {(!email.parsed_payer_name || email.processing_status === 'ignored') && <Typography variant="caption" display="block" color="text.secondary">{email.subject || 'No subject'} · {email.from_header || 'Unknown sender'}</Typography>}
                        </TableCell>
                        <TableCell align="right">{email.parsed_amount != null ? money(Number(email.parsed_amount)) : '-'}</TableCell>
                        <TableCell>{new Date(email.received_at).toLocaleString()}</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          {['needs_review', 'unmatched', 'parse_failed'].includes(email.processing_status) && !readOnly ? (
                            <FormControl size="small" fullWidth>
                              <InputLabel>Player</InputLabel>
                              <Select label="Player" value={selectedPlayerId} onChange={(event) => setManualMatchByEmailId((current) => ({ ...current, [email.id]: event.target.value }))}>
                                <MenuItem value="">Select player</MenuItem>
                                {lookups.players.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                              </Select>
                            </FormControl>
                          ) : (
                            formatPlayerName(playerById.get(email.matched_player_id || ''), userPreferences) || '-'
                          )}
                        </TableCell>
                        <TableCell>{email.matching_method || transaction?.matching_method || '-'}</TableCell>
                        <TableCell align="right">{email.match_confidence != null ? Number(email.match_confidence).toFixed(2) : '-'}</TableCell>
                        <TableCell>
                          {transaction ? `${money(Number(transaction.amount_allocated_to_fees || 0))} to fees${Number(transaction.amount_converted_to_credit || 0) > 0 ? `, ${money(Number(transaction.amount_converted_to_credit))} credit` : ''}` : '-'}
                          {allocations.length > 0 && <Typography variant="caption" display="block" color="text.secondary">{allocations.length} allocation{allocations.length === 1 ? '' : 's'}</Typography>}
                        </TableCell>
                        <TableCell><Chip size="small" label={processingStatusLabel(email.processing_status)} color={email.processing_status.includes('applied') ? 'success' : email.processing_status === 'duplicate' ? 'warning' : email.processing_status.includes('failed') ? 'error' : 'default'} /></TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography variant="body2">{email.error_summary || '-'}</Typography>
                          {(email.parser_warnings || []).map((warning) => <Typography key={warning} variant="caption" display="block" color="warning.main">{warning}</Typography>)}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="column" spacing={0.5} alignItems="flex-end">
                            {!readOnly && email.processing_status === 'parse_failed' && <Button size="small" startIcon={<ReplayIcon />} onClick={() => void retryPaymentEmailParse(email)}>Retry Parse</Button>}
                            {!readOnly && canManageEmailImport && isRecoverableIgnoredPayment(email) && <Button size="small" color="warning" startIcon={<ReplayIcon />} onClick={() => void retryPaymentEmailParse(email, true)}>Reparse Ignored</Button>}
                            {!readOnly && ['needs_review', 'unmatched', 'parse_failed'].includes(email.processing_status) && <Button size="small" variant="contained" onClick={() => void applyInboundPayment(email)}>Apply</Button>}
                            {!readOnly && <Button size="small" onClick={() => void markInboundPayment(email, 'duplicate')}>Mark Duplicate</Button>}
                            {!readOnly && <Button size="small" onClick={() => void markInboundPayment(email, 'ignored')}>Ignore</Button>}
                            {!readOnly && transaction && transaction.payment_status === 'applied' && !transaction.reversed_at && <Button size="small" color="error" onClick={() => void reverseImportedPayment(transaction)}>Reverse</Button>}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paymentInboxRows.length === 0 && <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No payment emails match this filter.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Alert severity="info">This staging feature is disabled by default. Start with Review Only mode and manually forward a sanitized Venmo or Zelle payment email during testing.</Alert>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Automatic Payment Email Import</Typography>
            <Stack spacing={2}>
              {workspacePaymentSettings.map((workspace) => (
                <Paper key={workspace.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{workspace.name}</Typography>
                        <Typography variant="body2" color="text.secondary">Forwarding address</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{paymentAddress(workspace)}</Typography>
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button startIcon={<ContentCopyIcon />} onClick={() => void copyPaymentAddress(workspace)}>Copy Address</Button>
                        {!readOnly && <Button color="warning" onClick={() => void regeneratePaymentAddress(workspace)}>Regenerate Address</Button>}
                      </Stack>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                      <FormControl fullWidth>
                        <InputLabel>Feature Status</InputLabel>
                        <Select label="Feature Status" value={workspace.payment_email_import_mode} disabled={readOnly} onChange={(event) => void updatePaymentImportMode(workspace.id, event.target.value as WorkspacePaymentSettings['payment_email_import_mode'])}>
                          <MenuItem value="disabled">Disabled</MenuItem>
                          <MenuItem value="review_only">Import for review only</MenuItem>
                          <MenuItem value="auto_exact">Automatically apply high-confidence matches</MenuItem>
                        </Select>
                      </FormControl>
                      <Box><Typography variant="caption" color="text.secondary">Last received</Typography><Typography variant="body2">{workspace.payment_email_last_received_at ? new Date(workspace.payment_email_last_received_at).toLocaleString() : '-'}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Last success</Typography><Typography variant="body2">{workspace.payment_email_last_success_at ? new Date(workspace.payment_email_last_success_at).toLocaleString() : '-'}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Last error</Typography><Typography variant="body2">{workspace.payment_email_last_error || '-'}</Typography></Box>
                    </Box>
                    <Alert severity="info">
                      Configure Cloudflare Email Routing so <strong>payments@courtcaptain.net</strong> goes to the staging Email Worker with plus addressing enabled. Then forward only Venmo/Zelle payment-received notifications to this workspace address.
                    </Alert>
                  </Stack>
                </Paper>
              ))}
              {workspacePaymentSettings.length === 0 && <Typography variant="body2" color="text.secondary">No admin-visible workspaces are available for payment email import.</Typography>}
            </Stack>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Verified Payment Aliases</Typography>
            {!readOnly && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.2fr 1fr 1.5fr auto' }, gap: 2, mb: 2 }}>
                <FormControl fullWidth><InputLabel>Workspace</InputLabel><Select label="Workspace" value={aliasForm.workspaceId} onChange={(event) => setAliasForm((current) => ({ ...current, workspaceId: event.target.value }))}>{workspacePaymentSettings.map((workspace) => <MenuItem key={workspace.id} value={workspace.id}>{workspace.name}</MenuItem>)}</Select></FormControl>
                <FormControl fullWidth><InputLabel>Player</InputLabel><Select label="Player" value={aliasForm.playerId} onChange={(event) => setAliasForm((current) => ({ ...current, playerId: event.target.value }))}>{lookups.players.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
                <FormControl fullWidth><InputLabel>Provider</InputLabel><Select label="Provider" value={aliasForm.provider} onChange={(event) => setAliasForm((current) => ({ ...current, provider: event.target.value as 'Venmo' | 'Zelle' | 'Any' }))}><MenuItem value="Venmo">Venmo</MenuItem><MenuItem value="Zelle">Zelle</MenuItem><MenuItem value="Any">Any</MenuItem></Select></FormControl>
                <TextField label="Alias / Sender Name" value={aliasForm.alias} onChange={(event) => setAliasForm((current) => ({ ...current, alias: event.target.value }))} />
                <Button variant="contained" onClick={() => void savePaymentAlias()}>Add Alias</Button>
              </Box>
            )}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Workspace</TableCell><TableCell sx={{ fontWeight: 700 }}>Player</TableCell><TableCell sx={{ fontWeight: 700 }}>Provider</TableCell><TableCell sx={{ fontWeight: 700 }}>Alias</TableCell><TableCell sx={{ fontWeight: 700 }}>Status</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell></TableRow></TableHead>
                <TableBody>
                  {paymentAliases.map((alias) => (
                    <TableRow key={alias.id}>
                      <TableCell>{optionLabel(lookups.workspaces, alias.workspace_id)}</TableCell>
                      <TableCell>{optionLabel(lookups.players, alias.player_id)}</TableCell>
                      <TableCell>{alias.provider || 'Any'}</TableCell>
                      <TableCell>{alias.alias_display_name}</TableCell>
                      <TableCell>{alias.is_active ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell align="right">{!readOnly && alias.is_active && <Button size="small" color="error" onClick={() => void deactivatePaymentAlias(alias)}>Deactivate</Button>}</TableCell>
                    </TableRow>
                  ))}
                  {paymentAliases.length === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No verified payment aliases yet.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}

      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setEditingCampaignId(null); }} maxWidth="lg" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle>{editingCampaignId ? 'Edit Fee Campaign' : 'Create Fee Campaign'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <TextField label="Fee Name" value={campaignForm.fee_name} required onChange={(event) => setCampaignForm((current) => ({ ...current, fee_name: event.target.value }))} />
              <FormControl required><InputLabel>Fee Type</InputLabel><Select label="Fee Type" value={campaignForm.fee_type} onChange={(event) => setCampaignForm((current) => ({ ...current, fee_type: event.target.value }))}>{feeTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
              <TextField label="Amount Per Player" type="number" value={campaignForm.amount_per_player} inputProps={{ min: 0, step: 0.01 }} onChange={(event) => setCampaignForm((current) => ({ ...current, amount_per_player: event.target.value }))} />
              <TextField label="Due Date" type="date" value={campaignForm.due_date} InputLabelProps={{ shrink: true }} onChange={(event) => setCampaignForm((current) => ({ ...current, due_date: event.target.value }))} />
              <FormControl sx={{ gridColumn: { md: '1 / 3' } }}><InputLabel>Charge Mode</InputLabel><Select label="Charge Mode" value={campaignForm.assignment_granularity} onChange={(event) => setCampaignForm((current) => ({ ...current, assignment_granularity: event.target.value as 'per_team' | 'per_player' }))}><MenuItem value="per_team">Per team roster spot</MenuItem><MenuItem value="per_player">Once per player</MenuItem></Select></FormControl>
              <TextField label="Description" value={campaignForm.description} multiline minRows={2} sx={{ gridColumn: { md: '1 / 3' } }} onChange={(event) => setCampaignForm((current) => ({ ...current, description: event.target.value }))} />
              <TextField label="Notes" value={campaignForm.notes} multiline minRows={2} sx={{ gridColumn: { md: '3 / 5' } }} onChange={(event) => setCampaignForm((current) => ({ ...current, notes: event.target.value }))} />
              <FormControlLabel control={<Checkbox checked={campaignForm.active_status} onChange={(event) => setCampaignForm((current) => ({ ...current, active_status: event.target.checked }))} />} label="Active" />
            </Box>
            {!editingCampaignId && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2 }}>
                  <FormControl><InputLabel>Assignment Scope</InputLabel><Select label="Assignment Scope" value={assignmentScope} onChange={(event) => setAssignmentScope(event.target.value as 'team' | 'teams' | 'players')}><MenuItem value="team">Entire Team</MenuItem><MenuItem value="teams">Multiple Teams</MenuItem><MenuItem value="players">Individual Players</MenuItem></Select></FormControl>
                  {assignmentScope === 'players' ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl fullWidth><InputLabel>Team Context</InputLabel><Select label="Team Context" value={individualTeamId} onChange={(event) => setIndividualTeamId(event.target.value)}><MenuItem value="">All roster teams</MenuItem>{teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}</Select></FormControl>
                      <FormControl fullWidth><InputLabel>Players</InputLabel><Select multiple label="Players" value={selectedPlayerIds} renderValue={(selected) => `${(selected as string[]).length} selected`} onChange={(event) => setSelectedPlayerIds(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}>{lookups.players.map((option) => <MenuItem key={option.value} value={option.value}><Checkbox checked={selectedPlayerIds.includes(option.value)} />{option.label}</MenuItem>)}</Select></FormControl>
                    </Stack>
                  ) : (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl fullWidth><InputLabel>Teams</InputLabel><Select multiple={assignmentScope === 'teams'} label="Teams" value={assignmentScope === 'teams' ? selectedTeamIds : selectedTeamIds[0] || ''} renderValue={(selected) => Array.isArray(selected) ? `${selected.length} selected` : optionLabel(lookups.teams, selected)} onChange={(event) => setSelectedTeamIds(typeof event.target.value === 'string' ? (assignmentScope === 'teams' ? event.target.value.split(',') : [event.target.value]) : event.target.value)}>{teams.map((team) => <MenuItem key={team.id} value={team.id}>{assignmentScope === 'teams' && <Checkbox checked={selectedTeamIds.includes(team.id)} />}{team.team_name}</MenuItem>)}</Select></FormControl>
                      {assignmentScope === 'teams' && <FormControlLabel control={<Checkbox checked={campaignForm.assignment_granularity === 'per_player'} onChange={(event) => setCampaignForm((current) => ({ ...current, assignment_granularity: event.target.checked ? 'per_player' : 'per_team' }))} />} label="Charge each player only once" />}
                    </Stack>
                  )}
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}><Table size="small" sx={{ minWidth: 520 }}><TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Player</TableCell><TableCell sx={{ fontWeight: 700 }}>Team</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Amount Owed</TableCell></TableRow></TableHead><TableBody>
                  {previewAssignments.map((assignment) => <TableRow key={assignment.key}><TableCell>{formatPlayerName(playerById.get(assignment.player_id), userPreferences) || optionLabel(lookups.players, assignment.player_id)}</TableCell><TableCell>{optionLabel(lookups.teams, assignment.team_id)}</TableCell><TableCell align="right">{money(assignment.amount_owed)}</TableCell></TableRow>)}
                  {previewAssignments.length === 0 && <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Select teams or players to preview assignments.</Typography></TableCell></TableRow>}
                </TableBody></Table></TableContainer>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => { setCreateOpen(false); setEditingCampaignId(null); }}>Cancel</Button><Button variant="contained" onClick={() => void saveCampaign()}>Save Campaign</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(paymentAssignmentId)} onClose={() => setPaymentAssignmentId(null)} maxWidth="sm" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {paymentAssignment && (
              <Alert severity={paymentOverpayment > 0 ? 'warning' : 'info'}>
                Balance due: {money(paymentAssignment.outstanding)}. Cash applied to this fee: {money(paymentAppliedCash)}. {paymentOverpayment > 0 ? `Overpayment: ${money(paymentOverpayment)}.` : ''}
              </Alert>
            )}
            <TextField label="Cash Received" type="number" value={paymentForm.amount} inputProps={{ min: 0.01, step: 0.01, inputMode: 'decimal' }} onChange={(event) => setPaymentForm((current) => ({ ...current, amount: event.target.value }))} />
            {paymentOverpayment > 0 && (
              <FormControl>
                <InputLabel>Overpayment Handling</InputLabel>
                <Select label="Overpayment Handling" value={paymentForm.overpayment_action} onChange={(event) => setPaymentForm((current) => ({ ...current, overpayment_action: event.target.value as 'retain_credit' | 'return' }))}>
                  <MenuItem value="retain_credit">Retain as Player Credit</MenuItem>
                  <MenuItem value="return">Returned to Player</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField label="Payment Date" type="date" value={paymentForm.payment_date} InputLabelProps={{ shrink: true }} onChange={(event) => setPaymentForm((current) => ({ ...current, payment_date: event.target.value }))} />
            <FormControl><InputLabel>Payment Method</InputLabel><Select label="Payment Method" value={paymentForm.payment_method} onChange={(event) => setPaymentForm((current) => ({ ...current, payment_method: event.target.value }))}>{paymentMethodOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
            <TextField label="Notes" value={paymentForm.notes} multiline minRows={2} onChange={(event) => setPaymentForm((current) => ({ ...current, notes: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setPaymentAssignmentId(null)}>Cancel</Button><Button variant="contained" onClick={() => void savePayment()}>Save Payment</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(addAssignmentCampaignId)} onClose={() => setAddAssignmentCampaignId(null)} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle>Add Player to Fee Campaign</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Team Context</InputLabel>
              <Select label="Team Context" value={addAssignmentForm.teamId} onChange={(event) => setAddAssignmentForm((current) => ({ ...current, teamId: event.target.value }))}>
                <MenuItem value="">All roster teams</MenuItem>
                {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Players</InputLabel>
              <Select
                multiple
                label="Players"
                value={addAssignmentForm.playerIds}
                renderValue={(selected) => `${(selected as string[]).length} selected`}
                onChange={(event) => setAddAssignmentForm((current) => ({ ...current, playerIds: typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value }))}
              >
                {lookups.players.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={addAssignmentForm.playerIds.includes(option.value)} />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Amount Owed" type="number" value={addAssignmentForm.amount} inputProps={{ min: 0, step: 0.01 }} onChange={(event) => setAddAssignmentForm((current) => ({ ...current, amount: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setAddAssignmentCampaignId(null)}>Cancel</Button><Button variant="contained" onClick={() => void saveAddedAssignments()}>Add Player</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(historyAssignmentId)} onClose={() => setHistoryAssignmentId(null)} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle>Payment History</DialogTitle>
        <DialogContent><TableContainer sx={{ overflowX: 'auto' }}><Table size="small" sx={{ minWidth: 720 }}><TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Date</TableCell><TableCell sx={{ fontWeight: 700 }}>Method</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Cash Received</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Cash Applied</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Credit Retained</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Returned</TableCell><TableCell sx={{ fontWeight: 700 }}>Notes</TableCell></TableRow></TableHead><TableBody>{selectedHistory.map((payment) => <TableRow key={payment.id}><TableCell>{payment.payment_date}</TableCell><TableCell>{payment.payment_method}</TableCell><TableCell align="right">{money(Number(payment.cash_received_amount ?? payment.amount ?? 0))}</TableCell><TableCell align="right">{money(Number(payment.fee_applied_cash_amount ?? payment.amount ?? 0))}</TableCell><TableCell align="right">{money(Number(payment.overpayment_credit_amount || 0))}</TableCell><TableCell align="right">{money(Number(payment.overpayment_returned_amount || 0))}</TableCell><TableCell>{payment.notes || '-'}</TableCell></TableRow>)}{selectedHistory.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No payments recorded.</Typography></TableCell></TableRow>}</TableBody></Table></TableContainer></DialogContent>
        <DialogActions><Button onClick={() => setHistoryAssignmentId(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(applyCreditAssignmentId)} onClose={closeApplyCreditDialog} maxWidth="sm" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle>Apply Player Credit</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {applyCreditAssignment && (
              <Alert severity="info">
                Available credit from {formatPlayerName(applyCreditSourcePlayer || undefined, userPreferences) || 'selected player'}: {money(applyCreditAvailable)}. Remaining fee balance for {formatPlayerName(playerById.get(applyCreditAssignment.player_id), userPreferences)}: {money(applyCreditAssignment.outstanding)}.
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Credit Source</InputLabel>
              <Select
                label="Credit Source"
                value={applyCreditForm.sourcePlayerId}
                onChange={(event) => {
                  const sourcePlayerId = String(event.target.value);
                  const selectedSource = applyCreditSources.find((source) => source.player_id === sourcePlayerId);
                  const availableCredit = Number(selectedSource?.credit_balance || 0);
                  const generatedDescription = buildApplyCreditDescription(sourcePlayerId, applyCreditAssignment);
                  setApplyCreditForm((current) => ({
                    ...current,
                    sourcePlayerId,
                    amount: applyCreditAssignment ? String(Math.min(applyCreditAssignment.outstanding, availableCredit)) : current.amount,
                    description: applyCreditDescriptionEdited ? current.description : generatedDescription,
                  }));
                }}
              >
                {applyCreditSources.map((source) => <MenuItem key={`${source.workspace_id}:${source.player_id}`} value={source.player_id}>{formatPlayerName(playerById.get(source.player_id), userPreferences)} - {money(Number(source.credit_balance || 0))} credit</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Credit Amount" type="number" value={applyCreditForm.amount} inputProps={{ min: 0.01, step: 0.01, inputMode: 'decimal' }} onChange={(event) => setApplyCreditForm((current) => ({ ...current, amount: event.target.value }))} />
            <TextField label="Description" value={applyCreditForm.description} placeholder="Credit applied to fee campaign" multiline minRows={2} onChange={(event) => {
              setApplyCreditDescriptionEdited(true);
              setApplyCreditForm((current) => ({ ...current, description: event.target.value }));
            }} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={closeApplyCreditDialog}>Cancel</Button><Button variant="contained" onClick={() => void saveApplyCredit()}>Apply Credit</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(ledgerPlayerId)} onClose={() => setLedgerPlayerId(null)} maxWidth="lg" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle>{selectedLedgerPlayer ? `${formatPlayerName(selectedLedgerPlayer, userPreferences)} Ledger` : 'Player Ledger'}</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Date</TableCell><TableCell sx={{ fontWeight: 700 }}>Description</TableCell><TableCell sx={{ fontWeight: 700 }}>Type</TableCell><TableCell sx={{ fontWeight: 700 }}>Source</TableCell><TableCell sx={{ fontWeight: 700 }}>Destination</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>Running Balance</TableCell><TableCell sx={{ fontWeight: 700 }}>User</TableCell></TableRow></TableHead>
              <TableBody>
                {selectedLedgerEntries.map((entry) => {
                  const runningBalance = ledgerEntries
                    .filter((candidate) => candidate.workspace_id === entry.workspace_id && candidate.player_id === entry.player_id && new Date(candidate.created_at).getTime() <= new Date(entry.created_at).getTime())
                    .reduce((sum, candidate) => sum + Number(candidate.amount || 0), 0);
                  return (
                    <TableRow key={entry.id} hover>
                      <TableCell>{new Date(entry.created_at).toLocaleString()}</TableCell>
                      <TableCell>{entry.description}</TableCell>
                      <TableCell>{entry.transaction_type.replaceAll('_', ' ')}</TableCell>
                      <TableCell>{entry.source_fee_campaign_id ? campaignById.get(entry.source_fee_campaign_id)?.fee_name || 'Source campaign' : '-'}</TableCell>
                      <TableCell>{entry.destination_fee_campaign_id ? campaignById.get(entry.destination_fee_campaign_id)?.fee_name || 'Destination campaign' : '-'}</TableCell>
                      <TableCell align="right"><Chip size="small" color={Number(entry.amount) > 0 ? 'success' : 'warning'} label={`${Number(entry.amount) > 0 ? '+' : ''}${money(Number(entry.amount || 0))}`} /></TableCell>
                      <TableCell align="right">{money(runningBalance)}</TableCell>
                      <TableCell>{optionLabel(lookups.app_users, entry.created_by_user_id) || '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {selectedLedgerEntries.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No ledger entries for this player.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={() => setLedgerPlayerId(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={creditAdjustmentOpen} onClose={() => setCreditAdjustmentOpen(false)} maxWidth="sm" fullWidth fullScreen={window.innerWidth < 600}>
        <DialogTitle>Manual Credit Adjustment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">This creates an immutable ledger entry. Historical ledger rows cannot be edited or deleted.</Alert>
            <FormControl fullWidth required><InputLabel>Workspace</InputLabel><Select label="Workspace" value={creditAdjustmentForm.workspaceId} onChange={(event) => setCreditAdjustmentForm((current) => ({ ...current, workspaceId: event.target.value }))}>{lookups.workspaces.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
            <FormControl fullWidth required><InputLabel>Player</InputLabel><Select label="Player" value={creditAdjustmentForm.playerId} onChange={(event) => setCreditAdjustmentForm((current) => ({ ...current, playerId: event.target.value }))}>{lookups.players.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>
            <FormControl fullWidth required><InputLabel>Action</InputLabel><Select label="Action" value={creditAdjustmentForm.transactionType} onChange={(event) => setCreditAdjustmentForm((current) => ({ ...current, transactionType: event.target.value }))}><MenuItem value="manual_credit">Add Credit</MenuItem><MenuItem value="credit_adjustment">Reduce Credit</MenuItem><MenuItem value="credit_refund">Record Refund</MenuItem><MenuItem value="credit_reversal">Reverse Prior Transaction</MenuItem></Select></FormControl>
            <TextField label="Amount" type="number" value={creditAdjustmentForm.amount} inputProps={{ min: 0.01, step: 0.01, inputMode: 'decimal' }} onChange={(event) => setCreditAdjustmentForm((current) => ({ ...current, amount: event.target.value }))} />
            <TextField label="Reason" value={creditAdjustmentForm.description} multiline minRows={2} required onChange={(event) => setCreditAdjustmentForm((current) => ({ ...current, description: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setCreditAdjustmentOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => void saveCreditAdjustment()}>Save Ledger Entry</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
